import React, { useCallback, useEffect, useState } from 'react';
import {
  Avatar,
  Button,
  Card,
  Checkbox,
  DatePicker,
  Descriptions,
  Empty,
  Form,
  Image,
  Input,
  InputNumber,
  Modal,
  Pagination,
  Popconfirm,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Tabs,
  Tag,
  Timeline,
  Tooltip,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { type Dayjs } from 'dayjs';
import {
  BarcodeOutlined,
  CalendarOutlined,
  CommentOutlined,
  DollarCircleOutlined,
  EditOutlined,
  EyeOutlined,
  StopOutlined,
  SearchOutlined,
  ShoppingCartOutlined,
  TeamOutlined,
  UserOutlined,
} from '@ant-design/icons';
import {
  serviceBookingOrderService,
  type AssignableStaffDateItem,
  type AssignableStaffListItem,
  type MeituanOrderQueryInfoReceipt,
  type MeituanOrderQueryInfoResponse,
  type MeituanPaymentSharesResponse,
  type MeituanReservationChangeRecord,
  type MeituanShopReviewItem,
  type MeituanShopReviewListResponse,
  type PreviewMeituanOrderByReservationNoResponse,
  type ServiceBookingOrder,
  type SubOrderSettlementListItem,
  type ServiceBookingOrderSubItem,
  type CorrectBasicInfoRequest,
  type CorrectPaymentTimeRequest,
  type UpdateSubOrderSettlementRequest,
} from '../../services/serviceBookingOrderService';
import { listMeituanShops, type MeituanShop } from '../../services/meituanShopService';
import { recoverMeituanReservationOrder } from '../../services/meituanReservationLogService';
import { getAllServiceCategories, type ServiceCategory } from '../../services/serviceCategoryService';
import { getServiceProducts, type ServiceProduct } from '../../services/serviceProductService';
import { normalizeFileUrl } from '../../services/fileService';
import { useHorizontalTableWheel } from '../../hooks/useHorizontalTableWheel';
import { useAppSelector } from '../../store';

const { Option } = Select;
const { RangePicker } = DatePicker;

const getDefaultAssignOrderItemIds = (items?: AssignableStaffDateItem[]) => {
  const list = (items || []).filter(item => item.orderItemId);
  const withoutConflict = list.filter(item => !item.hasTimeConflict).map(item => item.orderItemId);
  return withoutConflict.length ? withoutConflict : list.map(item => item.orderItemId);
};

const flattenCreateCategories = (list: ServiceCategory[], parentNames: string[] = []): Array<ServiceCategory & { displayName: string }> => {
  const result: Array<ServiceCategory & { displayName: string }> = [];
  list.forEach(item => {
    const names = [...parentNames, item.name];
    result.push({ ...item, displayName: names.join(' / ') });
    if (item.children?.length) {
      result.push(...flattenCreateCategories(item.children, names));
    }
  });
  return result;
};

const splitCreateShopIDs = (value?: string | string[]) => {
  if (Array.isArray(value)) {
    return value.map(item => String(item || '').trim()).filter(Boolean);
  }
  return String(value || '')
    .split(',')
    .map(item => item.trim())
    .filter(Boolean);
};

type OrderSearchValues = {
  source?: string;
  creationMode?: string;
  createdByUserId?: string;
  orderNo?: string;
  couponCode?: string;
  externalReservationNo?: string;
  externalOrderId?: string;
  contactName?: string;
  contactPhone?: string;
  address?: string;
  staffName?: string;
  staffPhone?: string;
  serviceDate?: { format: (template: string) => string };
  createdAt?: Array<{ format: (template: string) => string }>;
  completedAt?: Array<{ format: (template: string) => string }>;
  payStatus?: string;
  serviceProviderName?: string;
};

type MeituanMerchantModifyFormValues = {
  reserveUserName?: string;
  reservePhone?: string;
  reserveAddress?: string;
  provinceName?: string;
  cityName?: string;
  districtName?: string;
  addressDetail?: string;
  bookTimeRange?: Array<{ valueOf: () => number }>;
  deliveryUserName?: string;
  deliveryAddress?: string;
  deliveryPhone?: string;
};

type MeituanShopReviewSearchValues = {
  star?: number;
  platform?: number;
  reviewTimeRange?: Array<{ format: (template: string) => string }>;
};

type MeituanOrderInfoEditFormValues = {
  externalOrderId: string;
  externalReservationNo: string;
  externalShopId: string;
};

type MeituanAddPriceFormValues = {
  addPriceItemId?: string;
  addPriceUnitPriceYuan?: number;
  addPriceSize?: number;
};

type AfterSaleDeductionFormValues = {
  settlementAmount: number;
  remark?: string;
};

type CreateOrderFormValues = {
  source: string;
  productId: string;
  productName: string;
  categoryId: string;
  categoryName: string;
  contactName: string;
  contactPhone: string;
  provinceName?: string;
  cityName?: string;
  districtName?: string;
  addressDetail: string;
  petQuantity?: number;
  serviceStartAt: Dayjs;
  serviceEndAt?: Dayjs;
  totalAmount: number;
  paidAt?: Dayjs;
  remark?: string;
  externalOrderId?: string;
  externalReservationNo?: string;
  externalShopId?: string;
};

type CorrectPaymentTimeFormValues = {
  paidAt: Dayjs;
  reason: string;
};

type CorrectBasicInfoFormValues = {
  contactName: string;
  contactPhone: string;
  provinceName?: string;
  cityName?: string;
  districtName?: string;
  addressDetail: string;
  serviceStartAt: Dayjs;
  serviceEndAt?: Dayjs;
  remark?: string;
  reason: string;
};

const createOrderSourceOptions = [
  {
    value: 'wechat',
    title: '微信订单',
    description: '手动录入微信来源订单',
    icon: <ShoppingCartOutlined style={{ fontSize: 20, color: '#1677ff' }} />,
  },
  {
    value: 'meituan',
    title: '美团订单',
    description: '录入美团来源订单信息',
    icon: <BarcodeOutlined style={{ fontSize: 20, color: '#fa8c16' }} />,
  },
] as const;

const payStatusMap: Record<string, { color: string; text: string }> = {
  unpaid: { color: 'default', text: '待支付' },
  paid: { color: 'success', text: '已支付' },
};

const orderSourceMap: Record<string, { color: string; text: string }> = {
  wechat: { color: 'green', text: '微信' },
  meituan: { color: 'gold', text: '美团' },
};

const creationModeMap: Record<string, { color: string; text: string }> = {
  admin_manual: { color: 'purple', text: '后台手动添加' },
  wechat_app: { color: 'green', text: '微信端创建' },
  meituan_callback: { color: 'gold', text: '美团回调' },
  system_import: { color: 'blue', text: '系统导入' },
  unknown: { color: 'default', text: '历史/未知' },
};

const orderStatusMap: Record<string, { color: string; text: string }> = {
  pending: { color: 'default', text: '待派单' },
  assigned: { color: 'processing', text: '待接单确认' },
  waiting: { color: 'default', text: '待签到' },
  checkin_pending: { color: 'default', text: '待签到' },
  serving: { color: 'processing', text: '服务中' },
  done: { color: 'success', text: '已完成' },
  accepted_review: { color: 'success', text: '已验收' },
  cancelled: { color: 'error', text: '已取消' },
  aftersale: { color: 'warning', text: '售后中' },
  refunded: { color: 'purple', text: '已退款' },
};

const orderStatusTabs = [
  { key: 'all', label: '全部' },
  { key: 'unpaid', label: '待支付' },
  { key: 'pending', label: '待派单' },
  { key: 'assigned', label: '待接单确认' },
  { key: 'waiting', label: '待签到' },
  { key: 'serving', label: '服务中' },
  { key: 'done', label: '已完成' },
  { key: 'aftersale', label: '售后中' },
  { key: 'refunded', label: '已退款' },
  { key: 'cancelled', label: '已取消' },
];

const meituanFulfillStatusMap: Record<number, { color: string; text: string }> = {
  0: { color: 'default', text: '未回传' },
  40001: { color: 'processing', text: '已分配' },
  40002: { color: 'blue', text: '已到达' },
  41001: { color: 'success', text: '履约完成' },
};

const meituanPaymentShareTypeMap: Record<number, string> = {
  1: '验证金额（用户实付+平台优惠）',
  2: '抵用券',
  5: '积分',
  6: '立减',
  8: '商户抵用券',
  10: 'C端美团支付',
  12: '优惠代码',
  15: '美团立减',
  17: '商家立减',
  18: '美团商家立减',
  21: '次卡',
  22: '打折卡',
  23: 'B端美团支付',
  24: '全渠道会员券',
  25: 'pos支付',
  26: '线下认款平台',
  28: '商家折上折',
};

const meituanReviewStarMap: Record<number, string> = {
  50: '5星',
  45: '4.5星',
  40: '4星',
  35: '3.5星',
  30: '3星',
  25: '2.5星',
  20: '2星',
  15: '1.5星',
  10: '1星',
  5: '0.5星',
};

const meituanOrderInfoStatusMap: Record<number, { color: string; text: string }> = {
  1: { color: 'default', text: '初始状态' },
  2: { color: 'processing', text: '创建成功' },
  3: { color: 'warning', text: '订单取消' },
  4: { color: 'error', text: '订单终止' },
  5: { color: 'success', text: '订单完成' },
};

const meituanReceiptStatusMap: Record<number, { color: string; text: string }> = {
  1: { color: 'processing', text: '未使用/未过期' },
  2: { color: 'success', text: '已使用' },
  3: { color: 'warning', text: '未使用/已过期' },
  4: { color: 'default', text: '已退款（未消费）' },
  6: { color: 'purple', text: '已退款（已消费退）' },
};

const formatMeituanTimestamp = (value?: unknown) => {
  if (value === undefined || value === null || value === '') {
    return '-';
  }
  const timestamp = Number(value);
  if (!Number.isFinite(timestamp) || timestamp <= 0) {
    return String(value);
  }
  return dayjs(timestamp).format('YYYY-MM-DD HH:mm:ss');
};

const formatMeituanFenAmount = (value?: unknown) => {
  if (value === undefined || value === null || value === '') {
    return '-';
  }
  const amount = Number(value);
  if (!Number.isFinite(amount)) {
    return String(value);
  }
  return `￥${(amount / 100).toFixed(2)}`;
};

const renderMeituanAmountShareTags = (value?: Array<Record<string, unknown>>) => {
  if (!value || value.length === 0) {
    return '-';
  }
  return (
    <Space size={[4, 4]} wrap>
      {value.map((item, index) => {
        const label = meituanPaymentShareTypeMap[Number(item.amountType || 0)] || item.title || item.amountType || '-';
        const amount = formatMeituanFenAmount(item.value ?? item.amount);
        return (
          <Tag key={`${item.amountType || 'type'}-${index}`} color="blue" style={{ marginInlineEnd: 0 }}>
            {`${label}：${amount}`}
          </Tag>
        );
      })}
    </Space>
  );
};

const getMeituanFieldText = (record: Record<string, unknown> | undefined, key: string) => {
  const value = record?.[key];
  if (value === undefined || value === null || value === '') {
    return '-';
  }
  if (typeof value === 'boolean') {
    return value ? '是' : '否';
  }
  return String(value);
};

const getFirstMeituanReceiptCode = (response?: MeituanOrderQueryInfoResponse | null) => {
  const receipt = (response?.receiptList || response?.receipts || []).find(item => item.receiptCode || item.serialNumber);
  return String(receipt?.receiptCode || receipt?.serialNumber || '').trim();
};

const buildOrderCouponInfoFromMeituanResponse = (response?: MeituanOrderQueryInfoResponse | null) =>
  (response?.receiptList || response?.receipts || [])
    .map(item => ({
      couponCode: String(item.receiptCode || item.serialNumber || '').trim(),
      verified: Number(item.receiptStatus ?? item.status ?? 0) === 2 || Number(item.receiptStatus ?? item.status ?? 0) === 6,
      verifyStatus: Number(item.receiptStatus ?? item.status ?? 0),
      amount: Number(
        (item.amountShareList || []).reduce((sum, share) => sum + Number(share.value ?? share.amount ?? 0), 0),
      ) / 100,
    }))
    .filter(item => item.couponCode);

const renderOrderCouponInfo = (record: ServiceBookingOrder) => {
  const couponInfo = Array.isArray(record.couponInfo) ? record.couponInfo : [];
  if (couponInfo.length === 0) {
    return record.receiptCode ? <span>券码：{record.receiptCode}</span> : '--';
  }
  return (
    <Space orientation="vertical" size={4}>
      {couponInfo.map((item, index) => {
        const status = Number(item.verifyStatus || 0);
        const statusConfig = meituanReceiptStatusMap[status] || {
          color: item.verified ? 'success' : 'default',
          text: item.verified ? '已核销' : '未核销',
        };
        return (
          <div key={`${item.couponCode || 'coupon'}-${index}`} style={{ lineHeight: 1.6 }}>
            <div style={{ wordBreak: 'break-all' }}>券码：{item.couponCode || '-'}</div>
            <Space size={6} wrap>
              <Tag color={statusConfig.color}>{statusConfig.text}</Tag>
              <span>金额：¥{Number(item.amount || 0).toFixed(2)}</span>
            </Space>
          </div>
        );
      })}
    </Space>
  );
};

const renderMeituanReviewPairs = (items: Array<{ title: string; content?: string; score?: number; accurateScore?: number }>) => {
  if (!items || items.length === 0) {
    return '-';
  }
  return (
    <Space size={[4, 4]} wrap>
      {items.map((item, index) => (
        <Tag key={`${item.title}-${index}`} color="blue" style={{ marginInlineEnd: 0 }}>
          {item.title || '-'}：
          {item.content || meituanReviewStarMap[Number(item.accurateScore || 0)] || item.score || '-'}
        </Tag>
      ))}
    </Space>
  );
};

type MeituanQueryActionCardProps = {
  title: string;
  desc: string;
  icon: React.ReactNode;
  primary?: boolean;
  onClick: () => void;
};

const MeituanQueryActionCard: React.FC<MeituanQueryActionCardProps> = ({ title, desc, icon, primary, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      width: 'calc(50% - 8px)',
      minWidth: 220,
      border: primary ? '1px solid #1677ff' : '1px solid #d9e3f0',
      borderRadius: 14,
      padding: '16px 18px',
      background: primary
        ? 'linear-gradient(135deg, #1677ff 0%, #4096ff 100%)'
        : 'linear-gradient(180deg, #ffffff 0%, #f7fbff 100%)',
      boxShadow: primary ? '0 10px 24px rgba(22, 119, 255, 0.2)' : '0 8px 22px rgba(31, 62, 104, 0.08)',
      cursor: 'pointer',
      textAlign: 'left',
      display: 'flex',
      alignItems: 'center',
      gap: 14,
      transition: 'all 0.18s ease',
      color: primary ? '#fff' : '#1f2d3d',
    }}
  >
    <span
      style={{
        width: 42,
        height: 42,
        borderRadius: 12,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        background: primary ? 'rgba(255, 255, 255, 0.18)' : '#edf5ff',
        color: primary ? '#fff' : '#1677ff',
        fontSize: 20,
      }}
    >
      {icon}
    </span>
    <span style={{ minWidth: 0 }}>
      <span style={{ display: 'block', fontSize: 16, fontWeight: 700, lineHeight: 1.35 }}>{title}</span>
      <span
        style={{
          display: 'block',
          marginTop: 4,
          fontSize: 12,
          lineHeight: 1.45,
          color: primary ? 'rgba(255, 255, 255, 0.86)' : '#6b7c90',
        }}
      >
        {desc}
      </span>
    </span>
  </button>
);

const renderStatusTag = (
  value: string,
  mapper: Record<string, { color: string; text: string }>,
) => {
  const config = mapper[value] || { color: 'default', text: value || '-' };
  return <Tag color={config.color}>{config.text}</Tag>;
};

const renderOrderStatusTag = (record: Pick<ServiceBookingOrder, 'payStatus' | 'status'>) => {
  if (record.payStatus === 'unpaid') {
    return <Tag color="default">待支付</Tag>;
  }
  return renderStatusTag(record.status, orderStatusMap);
};

const renderMeituanFulfillStatusTag = (value?: number) => {
  const status = Number(value || 0);
  const config = meituanFulfillStatusMap[status] || { color: 'default', text: status ? String(status) : '未回传' };
  return <Tag color={config.color}>{config.text}</Tag>;
};

const renderMeituanAddPricePaidTag = (value?: number) => (
  Number(value || 0) === 1 ? <Tag color="success">已支付</Tag> : <Tag color="default">未支付</Tag>
);

const hiddenOrderColumnKeys = new Set([
  'meituanAddPriceAmount',
  'meituanAddPricePaid',
  'meituanAddPriceSuccessTime',
  'meituanAddPriceSerialNumber',
]);

const formatStaffDistance = (distanceKm?: number | null) => {
  if (typeof distanceKm !== 'number' || !Number.isFinite(distanceKm)) {
    return '距离未知';
  }
  return `距离${distanceKm.toFixed(1)}km`;
};

const isApprovedCertification = (status?: string) => ['approved', 'verified', 'passed', 'success'].includes(String(status || '').trim().toLowerCase());

const normalizeAssignReason = (value?: string) => {
  const text = String(value || '').trim();
  if (!text || /^[?\s]+$/.test(text) || text.includes('????')) {
    return '后台手动派单';
  }
  return text;
};

const fallbackServiceCategoryNames: Record<string, string> = {
  service_category_wechat_home: '微信上门服务',
  service_category_meituan_local: '美团本地服务',
};

const formatServiceProjectsText = (value?: string) => {
  const raw = String(value || '').trim();
  if (!raw) {
    return '-';
  }
  return raw
    .split(/[,，、;\s]+/)
    .map(item => item.trim())
    .filter(Boolean)
    .map(item => fallbackServiceCategoryNames[item] || item)
    .join('、') || '-';
};

type AssignableStaffFilters = {
  address: string;
  staffName: string;
  staffPhone: string;
  requireCertification: boolean;
  requireOrderMode: boolean;
  requireServiceCategory: boolean;
  requireServiceTime: boolean;
  requireDispatchDistance: boolean;
  excludeTimeConflict: boolean;
  excludeRejectedCurrentOrder: boolean;
  excludeTimedOutCurrentOrder: boolean;
};

const defaultAssignableStaffFilters: AssignableStaffFilters = {
  address: '',
  staffName: '',
  staffPhone: '',
  requireCertification: true,
  requireOrderMode: true,
  requireServiceCategory: true,
  requireServiceTime: true,
  requireDispatchDistance: true,
  excludeTimeConflict: true,
  excludeRejectedCurrentOrder: false,
  excludeTimedOutCurrentOrder: false,
};

const stripContactFromAddress = (address?: string, contactName?: string, contactPhone?: string) => {
  let result = (address || '').trim();
  const name = (contactName || '').trim();
  const phone = (contactPhone || '').trim();
  if (name && result.startsWith(name)) {
    result = result.slice(name.length).trim();
  }
  if (phone && result.startsWith(phone)) {
    result = result.slice(phone.length).trim();
  }
  return result;
};

const trimAddressPrefix = (address: string, prefix?: string) => {
  let result = address.trim();
  const cleanPrefix = (prefix || '').trim();
  if (!cleanPrefix) {
    return result;
  }
  while (result.startsWith(cleanPrefix)) {
    result = result.slice(cleanPrefix.length).trim();
  }
  return result;
};

const getOrderAddressParts = (order: ServiceBookingOrder) => {
  const snapshot = order.addressSnapshot;
  const provinceName = snapshot?.provinceName || '';
  const cityName = snapshot?.cityName || '';
  const districtName = snapshot?.districtName || '';
  let addressDetail = snapshot?.addressDetail || stripContactFromAddress(order.addressText, order.contactName, order.contactPhone);
  [provinceName, cityName, districtName].forEach(prefix => {
    addressDetail = trimAddressPrefix(addressDetail, prefix);
  });
  return { provinceName, cityName, districtName, addressDetail };
};

const buildFullReserveAddress = (values: MeituanMerchantModifyFormValues) => [
  values.provinceName,
  values.cityName,
  values.districtName,
  values.addressDetail,
].map(value => (value || '').trim()).filter(Boolean).join(' ');

const PaymentOrderPage: React.FC = () => {
  const { user } = useAppSelector(state => state.auth);
  const canManageOrderCorrections = ['admin', 'super_admin', 'disease_control'].includes(String(user?.role || '')) ||
    Boolean(user?.permissions?.includes('service_booking_order_correction'));
  const [orders, setOrders] = useState<ServiceBookingOrder[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 10, total: 0 });
  const [activeStatusTab, setActiveStatusTab] = useState('all');
  const [searchForm] = Form.useForm<OrderSearchValues>();
  const tableWrapRef = useHorizontalTableWheel();

  const [detailVisible, setDetailVisible] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [currentOrder, setCurrentOrder] = useState<ServiceBookingOrder | null>(null);
  const [serviceFlowVisible, setServiceFlowVisible] = useState(false);
  const [currentServiceFlowItem, setCurrentServiceFlowItem] = useState<ServiceBookingOrderSubItem | null>(null);
  const [settlementVisible, setSettlementVisible] = useState(false);
  const [settlementLoading, setSettlementLoading] = useState(false);
  const [settlementGenerating, setSettlementGenerating] = useState(false);
  const [settlementSubmitting, setSettlementSubmitting] = useState(false);
  const [settlementEditVisible, setSettlementEditVisible] = useState(false);
  const [settlementEditSubmitting, setSettlementEditSubmitting] = useState(false);
  const [afterSaleDeductionVisible, setAfterSaleDeductionVisible] = useState(false);
  const [afterSaleDeductionSubmitting, setAfterSaleDeductionSubmitting] = useState(false);
  const [currentSettlementItem, setCurrentSettlementItem] = useState<ServiceBookingOrderSubItem | null>(null);
  const [subOrderSettlements, setSubOrderSettlements] = useState<SubOrderSettlementListItem[]>([]);
  const [currentSettlementRecord, setCurrentSettlementRecord] = useState<SubOrderSettlementListItem | null>(null);

  const [assignVisible, setAssignVisible] = useState(false);
  const [assignSubmitting, setAssignSubmitting] = useState(false);
  const [unassignSubmittingId, setUnassignSubmittingId] = useState('');
  const [cancelSubmittingId, setCancelSubmittingId] = useState('');
  const [meituanConfirmSubmittingId, setMeituanConfirmSubmittingId] = useState('');
  const [meituanConfirmVisible, setMeituanConfirmVisible] = useState(false);
  const [meituanConfirmOrder, setMeituanConfirmOrder] = useState<ServiceBookingOrder | null>(null);
  const [meituanRejectReason, setMeituanRejectReason] = useState('后台手动拒单');
  const [meituanRejectSubmittingId, setMeituanRejectSubmittingId] = useState('');
  const [meituanQueryVisible, setMeituanQueryVisible] = useState(false);
  const [meituanQueryOrder, setMeituanQueryOrder] = useState<ServiceBookingOrder | null>(null);
  const [meituanAmountQueryVisible, setMeituanAmountQueryVisible] = useState(false);
  const [meituanPaymentSharesLoading, setMeituanPaymentSharesLoading] = useState(false);
  const [meituanPaymentSharesResult, setMeituanPaymentSharesResult] = useState<MeituanPaymentSharesResponse | null>(null);
  const [meituanCouponQueryVisible, setMeituanCouponQueryVisible] = useState(false);
  const [meituanOrderInfoLoading, setMeituanOrderInfoLoading] = useState(false);
  const [meituanOrderInfoResult, setMeituanOrderInfoResult] = useState<MeituanOrderQueryInfoResponse | null>(null);
  const [meituanOrderInfoEditVisible, setMeituanOrderInfoEditVisible] = useState(false);
  const [meituanOrderInfoEditSubmitting, setMeituanOrderInfoEditSubmitting] = useState(false);
  const [meituanOrderInfoEditShops, setMeituanOrderInfoEditShops] = useState<MeituanShop[]>([]);
  const [meituanOrderInfoEditShopLoading, setMeituanOrderInfoEditShopLoading] = useState(false);
  const [meituanCommentVisible, setMeituanCommentVisible] = useState(false);
  const [meituanCommentLoading, setMeituanCommentLoading] = useState(false);
  const [meituanCommentResult, setMeituanCommentResult] = useState<MeituanShopReviewListResponse | null>(null);
  const [meituanCommentLocalFilterEnabled, setMeituanCommentLocalFilterEnabled] = useState(false);
  const [meituanCommentPagination, setMeituanCommentPagination] = useState({ page: 1, pageSize: 10 });
  const [meituanCancelVisible, setMeituanCancelVisible] = useState(false);
  const [meituanCancelSubmitting, setMeituanCancelSubmitting] = useState(false);
  const [meituanCancelReason, setMeituanCancelReason] = useState('');
  const [meituanCancelType, setMeituanCancelType] = useState(2);
  const [meituanMerchantModifyVisible, setMeituanMerchantModifyVisible] = useState(false);
  const [meituanMerchantModifySubmitting, setMeituanMerchantModifySubmitting] = useState(false);
  const [meituanAddPriceVisible, setMeituanAddPriceVisible] = useState(false);
  const [meituanAddPriceSubmitting, setMeituanAddPriceSubmitting] = useState(false);
  const [meituanChangeVisible, setMeituanChangeVisible] = useState(false);
  const [meituanChangeLoading, setMeituanChangeLoading] = useState(false);
  const [meituanChangeSubmitting, setMeituanChangeSubmitting] = useState(false);
  const [meituanChangeRecord, setMeituanChangeRecord] = useState<MeituanReservationChangeRecord | null>(null);
  const [createVisible, setCreateVisible] = useState(false);
  const [createSubmitting, setCreateSubmitting] = useState(false);
  const [createCategoryLoading, setCreateCategoryLoading] = useState(false);
  const [createProductLoading, setCreateProductLoading] = useState(false);
  const [createMeituanShopLoading, setCreateMeituanShopLoading] = useState(false);
  const [createMeituanPreviewLoading, setCreateMeituanPreviewLoading] = useState(false);
  const [createCategories, setCreateCategories] = useState<Array<ServiceCategory & { displayName: string }>>([]);
  const [createProducts, setCreateProducts] = useState<ServiceProduct[]>([]);
  const [createMeituanShops, setCreateMeituanShops] = useState<MeituanShop[]>([]);
  const [createMeituanPreview, setCreateMeituanPreview] = useState<PreviewMeituanOrderByReservationNoResponse | null>(null);
  const [meituanRecoverVisible, setMeituanRecoverVisible] = useState(false);
  const [meituanRecoverSubmitting, setMeituanRecoverSubmitting] = useState(false);
  const [meituanRecoverForm] = Form.useForm<{ meituanOrderId: string }>();
  const [staffLoading, setStaffLoading] = useState(false);
  const [staffOptions, setStaffOptions] = useState<AssignableStaffListItem[]>([]);
  const [staffPagination, setStaffPagination] = useState({ page: 1, pageSize: 10, total: 0 });
  const [staffFilters, setStaffFilters] = useState<AssignableStaffFilters>(defaultAssignableStaffFilters);
  const [assignForm] = Form.useForm<{ assignedStaffId: string; assignReason: string; noNeedStaffAccept: boolean }>();
  const [meituanMerchantModifyForm] = Form.useForm<MeituanMerchantModifyFormValues>();
  const [meituanAddPriceForm] = Form.useForm<MeituanAddPriceFormValues>();
  const [meituanOrderInfoEditForm] = Form.useForm<MeituanOrderInfoEditFormValues>();
  const [meituanCommentForm] = Form.useForm<MeituanShopReviewSearchValues>();
  const [createForm] = Form.useForm<CreateOrderFormValues>();
  const [paymentTimeCorrectionForm] = Form.useForm<CorrectPaymentTimeFormValues>();
  const [paymentTimeCorrectionVisible, setPaymentTimeCorrectionVisible] = useState(false);
  const [paymentTimeCorrectionSubmitting, setPaymentTimeCorrectionSubmitting] = useState(false);
  const [basicInfoCorrectionForm] = Form.useForm<CorrectBasicInfoFormValues>();
  const [basicInfoCorrectionVisible, setBasicInfoCorrectionVisible] = useState(false);
  const [basicInfoCorrectionSubmitting, setBasicInfoCorrectionSubmitting] = useState(false);
  const [settlementEditForm] = Form.useForm<{
    settlementAmount: number;
    completedAt: Dayjs;
    eligibleSettleAt: Dayjs;
  }>();
  const [afterSaleDeductionForm] = Form.useForm<AfterSaleDeductionFormValues>();
  const createSource = Form.useWatch('source', createForm);
  const createCategoryId = Form.useWatch('categoryId', createForm);

  const buildQueryParams = useCallback((params: OrderSearchValues = {}, statusTab = activeStatusTab) => {
    const nextParams = { ...params } as Record<string, unknown>;
    if (params.serviceDate) {
      nextParams.serviceDate = params.serviceDate.format('YYYY-MM-DD');
    }
    if (params.createdAt?.[0] && params.createdAt?.[1]) {
      nextParams.createdAtStart = params.createdAt[0].format('YYYY-MM-DD');
      nextParams.createdAtEnd = params.createdAt[1].format('YYYY-MM-DD');
    }
    delete nextParams.createdAt;
    if (params.completedAt?.[0] && params.completedAt?.[1]) {
      nextParams.completedAtStart = params.completedAt[0].format('YYYY-MM-DD');
      nextParams.completedAtEnd = params.completedAt[1].format('YYYY-MM-DD');
    }
    delete nextParams.completedAt;

    delete nextParams.status;
    if (statusTab === 'unpaid') {
      nextParams.status = 'pending';
      nextParams.payStatus = 'unpaid';
    } else if (statusTab === 'pending') {
      nextParams.status = 'pending';
      nextParams.payStatus = 'paid';
    } else if (statusTab === 'done') {
      nextParams.status = 'done,accepted_review';
    } else if (statusTab === 'waiting') {
      nextParams.status = 'waiting,checkin_pending';
    } else if (statusTab !== 'all') {
      nextParams.status = statusTab;
    }
    if (user?.isProvider && user.id) {
      nextParams.serviceProviderId = user.id;
    }

    return nextParams;
  }, [activeStatusTab, user?.id, user?.isProvider]);

  const loadOrders = useCallback(async (page = 1, pageSize = 10, params: OrderSearchValues = {}, statusTab = activeStatusTab) => {
    setLoading(true);
    try {
      const response = await serviceBookingOrderService.list({ page, pageSize, ...buildQueryParams(params, statusTab) });
      setOrders(response.list || []);
      setPagination({
        page: response.page || page,
        pageSize: response.pageSize || pageSize,
        total: response.total || 0,
      });
    } catch {
      message.error('加载订单失败');
    } finally {
      setLoading(false);
    }
  }, [activeStatusTab, buildQueryParams]);

  const loadStaffOptions = useCallback(async (
    orderId: string,
    page = 1,
    pageSize = 10,
    filters: AssignableStaffFilters = staffFilters,
  ) => {
    setStaffLoading(true);
    try {
      const response = await serviceBookingOrderService.listAssignableStaff(orderId, {
        page,
        pageSize,
        onlyAssignable: false,
        ...filters,
      });
      setStaffOptions(response.list || []);
      setStaffPagination({
        page: response.page || page,
        pageSize: response.pageSize || pageSize,
        total: response.total || 0,
      });
    } catch {
      setStaffOptions([]);
      setStaffPagination(prev => ({ ...prev, page, pageSize, total: 0 }));
      message.error('加载可派服务人员失败');
    } finally {
      setStaffLoading(false);
    }
  }, [staffFilters]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  useEffect(() => {
    if (!createVisible || !createSource) {
      return;
    }
    let active = true;
    const loadCreateCategories = async () => {
      setCreateCategoryLoading(true);
      try {
        const response = await getAllServiceCategories();
        const categoryList = flattenCreateCategories(response.data || [])
          .filter(item => item.source === createSource && Number(item.status || 0) === 1);
        if (!active) {
          return;
        }
        setCreateCategories(categoryList);
      } catch {
        if (active) {
          setCreateCategories([]);
          message.error('加载服务分类失败');
        }
      } finally {
        if (active) {
          setCreateCategoryLoading(false);
        }
      }
    };
    loadCreateCategories();
    return () => {
      active = false;
    };
  }, [createVisible, createSource]);

  useEffect(() => {
    if (!createVisible || !createSource) {
      return;
    }
    let active = true;
    const loadCreateProducts = async () => {
      setCreateProductLoading(true);
      try {
        const response = await getServiceProducts({
          page: 1,
          pageSize: 200,
          source: createSource as 'wechat' | 'meituan',
          status: 1,
          categoryId: createCategoryId || undefined,
        });
        if (!active) {
          return;
        }
        setCreateProducts(response.data?.list || []);
      } catch {
        if (active) {
          setCreateProducts([]);
          message.error('加载服务产品失败');
        }
      } finally {
        if (active) {
          setCreateProductLoading(false);
        }
      }
    };
    loadCreateProducts();
    return () => {
      active = false;
    };
  }, [createVisible, createSource, createCategoryId]);

  useEffect(() => {
    if (!createVisible || createSource !== 'meituan') {
      return;
    }
    let active = true;
    const loadCreateMeituanShops = async () => {
      setCreateMeituanShopLoading(true);
      try {
        const response = await listMeituanShops({ page: 1, pageSize: 1000, status: '1' });
        if (!active) {
          return;
        }
        const allShops = response.data?.list || [];
        const boundShopIDs = new Set(splitCreateShopIDs(user?.meituanShopIds));
        const filteredShops = user?.isProvider
          ? allShops.filter(shop => boundShopIDs.has(String(shop.id)))
          : allShops;
        setCreateMeituanShops(filteredShops);

        const currentShopId = String(createForm.getFieldValue('externalShopId') || '').trim();
        const hasCurrentShop = filteredShops.some(shop => String(shop.shopId || '').trim() === currentShopId);
        if (!hasCurrentShop) {
          createForm.setFieldValue('externalShopId', filteredShops.length === 1 ? filteredShops[0].shopId : undefined);
          setCreateMeituanPreview(null);
        }
      } catch {
        if (active) {
          setCreateMeituanShops([]);
          message.error('加载美团门店失败');
        }
      } finally {
        if (active) {
          setCreateMeituanShopLoading(false);
        }
      }
    };
    loadCreateMeituanShops();
    return () => {
      active = false;
    };
  }, [createVisible, createSource, createForm, user?.isProvider, user?.meituanShopIds]);

  const handleSearch = () => {
    const values = searchForm.getFieldsValue();
    loadOrders(1, pagination.pageSize, values, activeStatusTab);
  };

  const handleReset = () => {
    searchForm.resetFields();
    setActiveStatusTab('all');
    loadOrders(1, pagination.pageSize, {}, 'all');
  };

  const handleStatusTabChange = (status: string) => {
    setActiveStatusTab(status);
    loadOrders(1, pagination.pageSize, searchForm.getFieldsValue(), status);
  };

  const handleRecoverMeituanReservationOrder = async () => {
    const values = await meituanRecoverForm.validateFields();
    const meituanOrderId = String(values.meituanOrderId || '').trim();
    if (!meituanOrderId) {
      message.warning('请输入美团预约单号');
      return;
    }
    setMeituanRecoverSubmitting(true);
    try {
      const response = await recoverMeituanReservationOrder(meituanOrderId);
      const serviceOrderNo = response.data?.serviceOrderNo || '';
      message.success(serviceOrderNo ? `恢复成功，平台订单号：${serviceOrderNo}` : '恢复成功');
      setMeituanRecoverVisible(false);
      meituanRecoverForm.resetFields();
      loadOrders(1, pagination.pageSize, searchForm.getFieldsValue(), activeStatusTab);
    } catch {
      // apiClient 已统一提示错误
    } finally {
      setMeituanRecoverSubmitting(false);
    }
  };

  const handleOpenCreateModal = () => {
    setCreateCategories([]);
    setCreateProducts([]);
    setCreateMeituanShops([]);
    setCreateMeituanPreview(null);
    createForm.setFieldsValue({
      source: 'wechat',
      petQuantity: 1,
      totalAmount: 0,
      categoryId: undefined,
      categoryName: '',
      productId: undefined,
      productName: '',
    });
    setCreateVisible(true);
  };

  const handleCreateSourceChange = (value: string) => {
    createForm.setFieldsValue({
      source: value,
      categoryId: undefined,
      categoryName: '',
      productId: undefined,
      productName: '',
      externalOrderId: '',
      externalReservationNo: '',
      externalShopId: '',
    });
    setCreateProducts([]);
    setCreateMeituanShops([]);
    setCreateMeituanPreview(null);
  };

  const handleCreateCategoryChange = (value: string) => {
    const category = createCategories.find(item => item.id === value);
    createForm.setFieldsValue({
      categoryId: value,
      categoryName: category?.name || '',
      productId: undefined,
      productName: '',
    });
  };

  const handleCreateProductChange = (value: string) => {
    const product = createProducts.find(item => item.id === value);
    const category = createCategories.find(item => item.id === product?.categoryId);
    createForm.setFieldsValue({
      productId: value,
      productName: product?.name || '',
      categoryId: product?.categoryId || createForm.getFieldValue('categoryId'),
      categoryName: category?.name || createForm.getFieldValue('categoryName') || '',
      totalAmount: Number(product?.price || 0) || createForm.getFieldValue('totalAmount'),
    });
  };

  const handleCreateMeituanShopChange = (value: string) => {
    createForm.setFieldValue('externalShopId', value);
    setCreateMeituanPreview(null);
  };

  const handleCreateOrder = async () => {
    try {
      const values = await createForm.validateFields() as CreateOrderFormValues;
      setCreateSubmitting(true);
      await serviceBookingOrderService.create({
        ...values,
        provinceName: values.provinceName || '',
        cityName: values.cityName || '',
        districtName: values.districtName || '',
        petQuantity: Number(values.petQuantity || 1),
        serviceStartAt: values.serviceStartAt.format('YYYY-MM-DD HH:mm'),
        serviceEndAt: values.serviceEndAt ? values.serviceEndAt.format('YYYY-MM-DD HH:mm') : undefined,
        totalAmount: Number(values.totalAmount || 0),
        unitPrice: Number(values.totalAmount || 0),
        paidAt: values.paidAt ? values.paidAt.format('YYYY-MM-DD HH:mm:ss') : undefined,
      });
      message.success('添加订单成功');
      setCreateVisible(false);
      createForm.resetFields();
      loadOrders(1, pagination.pageSize, searchForm.getFieldsValue(), activeStatusTab);
    } catch (error: any) {
      if (!error?.errorFields) {
        message.error('添加订单失败');
      }
    } finally {
      setCreateSubmitting(false);
    }
  };

  const openPaymentTimeCorrection = () => {
    if (!currentOrder || currentOrder.creationMode !== 'admin_manual') {
      return;
    }
    paymentTimeCorrectionForm.setFieldsValue({
      paidAt: currentOrder.paidAt ? dayjs(currentOrder.paidAt) : dayjs(),
      reason: '',
    });
    setPaymentTimeCorrectionVisible(true);
  };

  const handlePaymentTimeCorrection = async () => {
    if (!currentOrder) return;
    try {
      const values = await paymentTimeCorrectionForm.validateFields();
      setPaymentTimeCorrectionSubmitting(true);
      const payload: CorrectPaymentTimeRequest = {
        paidAt: values.paidAt.format('YYYY-MM-DD HH:mm:ss'),
        reason: values.reason.trim(),
      };
      const updated = await serviceBookingOrderService.correctPaymentTime(currentOrder.id, payload);
      setCurrentOrder(updated);
      setOrders(prev => prev.map(item => item.id === updated.id ? updated : item));
      setPaymentTimeCorrectionVisible(false);
      message.success('支付时间更正成功');
    } catch (error: any) {
      if (!error?.errorFields) {
        message.error('支付时间更正失败');
      }
    } finally {
      setPaymentTimeCorrectionSubmitting(false);
    }
  };

  const openBasicInfoCorrection = () => {
    if (!currentOrder) return;
    const address = currentOrder.addressSnapshot;
    const [startText = '', endText = ''] = String(currentOrder.serviceTimeDesc || '').split('-');
    const serviceDate = currentOrder.serviceDateStart || '';
    basicInfoCorrectionForm.setFieldsValue({
      contactName: currentOrder.contactName || '',
      contactPhone: currentOrder.contactPhone || '',
      provinceName: address?.provinceName || '',
      cityName: address?.cityName || '',
      districtName: address?.districtName || '',
      addressDetail: address?.addressDetail || '',
      serviceStartAt: serviceDate && startText ? dayjs(`${serviceDate}T${startText}:00`) : undefined,
      serviceEndAt: serviceDate && endText ? dayjs(`${serviceDate}T${endText}:00`) : undefined,
      remark: currentOrder.remark || '',
      reason: '',
    });
    setBasicInfoCorrectionVisible(true);
  };

  const handleBasicInfoCorrection = async () => {
    if (!currentOrder) return;
    try {
      const values = await basicInfoCorrectionForm.validateFields();
      setBasicInfoCorrectionSubmitting(true);
      const payload: CorrectBasicInfoRequest = {
        contactName: values.contactName.trim(),
        contactPhone: values.contactPhone.trim(),
        provinceName: values.provinceName?.trim(),
        cityName: values.cityName?.trim(),
        districtName: values.districtName?.trim(),
        addressDetail: values.addressDetail.trim(),
        serviceStartAt: values.serviceStartAt.format('YYYY-MM-DD HH:mm'),
        serviceEndAt: values.serviceEndAt?.format('YYYY-MM-DD HH:mm'),
        remark: values.remark?.trim(),
        reason: values.reason.trim(),
      };
      const updated = await serviceBookingOrderService.correctBasicInfo(currentOrder.id, payload);
      setCurrentOrder(updated);
      setOrders(prev => prev.map(item => item.id === updated.id ? updated : item));
      setBasicInfoCorrectionVisible(false);
      message.success('订单基础信息更正成功');
    } catch (error: any) {
      if (!error?.errorFields) {
        message.error('订单基础信息更正失败');
      }
    } finally {
      setBasicInfoCorrectionSubmitting(false);
    }
  };

  const handleViewDetail = async (record: ServiceBookingOrder) => {
    setDetailVisible(true);
    setDetailLoading(true);
    try {
      const detail = await serviceBookingOrderService.get(record.id);
      setCurrentOrder(detail);
    } catch {
      message.error('加载订单详情失败');
      setDetailVisible(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const openAssignModal = (record: ServiceBookingOrder) => {
    setCurrentOrder(record);
    setStaffOptions([]);
    setStaffPagination({ page: 1, pageSize: 10, total: 0 });
    setStaffFilters(defaultAssignableStaffFilters);
    assignForm.setFieldsValue({
      assignedStaffId: record.assignedStaffId || undefined,
      assignReason: normalizeAssignReason(record.dispatchRemark),
      noNeedStaffAccept: true,
    });
    setAssignVisible(true);
    loadStaffOptions(record.id, 1, 10, defaultAssignableStaffFilters);
  };

  const openServiceFlowModal = (record: ServiceBookingOrderSubItem) => {
    setCurrentServiceFlowItem(record);
    setServiceFlowVisible(true);
  };

  const openSettlementModal = async (record: ServiceBookingOrderSubItem) => {
    setCurrentSettlementItem(record);
    setSettlementVisible(true);
    setSettlementLoading(true);
    try {
      const response = await serviceBookingOrderService.listSubOrderSettlements(record.id);
      const list = response.list || [];
      setSubOrderSettlements(list);
      setCurrentSettlementRecord(list[0] || null);
    } catch {
      setSubOrderSettlements([]);
      setCurrentSettlementRecord(null);
      message.error('加载结算流水失败');
    } finally {
      setSettlementLoading(false);
    }
  };

  const handleGenerateSettlement = async () => {
    if (!currentSettlementItem?.id) {
      return;
    }
    try {
      setSettlementGenerating(true);
      await serviceBookingOrderService.generateSubOrderSettlement(currentSettlementItem.id);
      message.success('生成结算流水成功');
      const response = await serviceBookingOrderService.listSubOrderSettlements(currentSettlementItem.id);
      const list = response.list || [];
      setSubOrderSettlements(list);
      setCurrentSettlementRecord(list[0] || null);
    } catch {
      message.error('生成结算流水失败');
    } finally {
      setSettlementGenerating(false);
    }
  };

  const hasNormalSettlement = subOrderSettlements.some(
    item => String(item.flowType || 'normal').trim() !== 'after_sale_deduction',
  );

  const canGenerateSettlement = Boolean(
    currentSettlementItem &&
    !hasNormalSettlement &&
    ['done', 'accepted_review'].includes(String(currentSettlementItem.status || '').trim()),
  );

  const canSettleSettlement = Boolean(
    currentSettlementItem && currentSettlementRecord && String(currentSettlementRecord.status || '').trim() === 'pending',
  );

  const canEditSettlement = Boolean(
    currentSettlementItem &&
    currentSettlementRecord &&
    String(currentSettlementRecord.status || '').trim() === 'pending' &&
    String(currentSettlementRecord.flowType || 'normal').trim() !== 'after_sale_deduction',
  );

  const openSettlementEditModal = () => {
    if (!currentSettlementRecord) {
      return;
    }
    settlementEditForm.setFieldsValue({
      settlementAmount: Number(currentSettlementRecord.settlementAmount || 0),
      completedAt: currentSettlementRecord.completedAt ? dayjs(currentSettlementRecord.completedAt) : undefined,
      eligibleSettleAt: currentSettlementRecord.eligibleSettleAt ? dayjs(currentSettlementRecord.eligibleSettleAt) : undefined,
    });
    setSettlementEditVisible(true);
  };

  const handleSubmitSettlementEdit = async () => {
    if (!currentSettlementItem?.id) {
      return;
    }
    try {
      const values = await settlementEditForm.validateFields();
      const payload: UpdateSubOrderSettlementRequest = {
        settlementId: String(currentSettlementRecord?.id || '').trim(),
        settlementAmount: Number(values.settlementAmount || 0),
        completedAt: values.completedAt.format('YYYY-MM-DD HH:mm:ss'),
        eligibleSettleAt: values.eligibleSettleAt.format('YYYY-MM-DD HH:mm:ss'),
      };
      setSettlementEditSubmitting(true);
      await serviceBookingOrderService.updateSubOrderSettlement(currentSettlementItem.id, payload);
      message.success('结算流水更新成功');
      setSettlementEditVisible(false);
      const response = await serviceBookingOrderService.listSubOrderSettlements(currentSettlementItem.id);
      const list = response.list || [];
      setSubOrderSettlements(list);
      setCurrentSettlementRecord(list.find(item => item.id === payload.settlementId) || list[0] || null);
    } catch (error) {
      if ((error as { errorFields?: unknown[] })?.errorFields) {
        return;
      }
      message.error('结算流水更新失败');
    } finally {
      setSettlementEditSubmitting(false);
    }
  };

  const handleSettleSettlement = async () => {
    if (!currentSettlementItem?.id) {
      return;
    }
    try {
      setSettlementSubmitting(true);
      await serviceBookingOrderService.settleSubOrderSettlementById(
        currentSettlementItem.id,
        String(currentSettlementRecord?.id || '').trim(),
      );
      message.success('结算成功，已结转到服务人员余额');
      const response = await serviceBookingOrderService.listSubOrderSettlements(currentSettlementItem.id);
      const list = response.list || [];
      setSubOrderSettlements(list);
      setCurrentSettlementRecord(list.find(item => item.id === currentSettlementRecord?.id) || list[0] || null);
    } catch {
      message.error('结算失败');
    } finally {
      setSettlementSubmitting(false);
    }
  };

  const openAfterSaleDeductionModal = (record: ServiceBookingOrderSubItem) => {
    setCurrentSettlementItem(record);
    afterSaleDeductionForm.setFieldsValue({
      settlementAmount: undefined as unknown as number,
      remark: '售后扣费',
    });
    setAfterSaleDeductionVisible(true);
  };

  const handleSubmitAfterSaleDeduction = async () => {
    if (!currentSettlementItem?.id) {
      return;
    }
    try {
      const values = await afterSaleDeductionForm.validateFields();
      const amount = Number(values.settlementAmount || 0);
      if (!(amount < 0)) {
        message.error('售后扣费金额必须为负数');
        return;
      }
      setAfterSaleDeductionSubmitting(true);
      await serviceBookingOrderService.createAfterSaleDeductionSettlement(currentSettlementItem.id, {
        settlementAmount: amount,
        remark: String(values.remark || '').trim() || '售后扣费',
      });
      message.success('售后扣费流水创建成功');
      setAfterSaleDeductionVisible(false);
      if (settlementVisible) {
        const response = await serviceBookingOrderService.listSubOrderSettlements(currentSettlementItem.id);
        const list = response.list || [];
        setSubOrderSettlements(list);
        setCurrentSettlementRecord(list[0] || null);
      }
    } catch (error) {
      if ((error as { errorFields?: unknown[] })?.errorFields) {
        return;
      }
      message.error((error as any)?.response?.data?.message || '售后扣费流水创建失败');
    } finally {
      setAfterSaleDeductionSubmitting(false);
    }
  };

  const renderServiceFlowMedia = (urls?: string[]) => {
    const list = (urls || []).filter(Boolean);
    if (!list.length) return null;
    const isVideo = (url: string) => /\.(mp4|mov|m4v|webm|ogg)(\?.*)?$/i.test(url);

    return (
      <Space size={[8, 8]} wrap style={{ marginTop: 8 }}>
        {list.map((url, index) => {
          const fullUrl = normalizeFileUrl(url);
          if (isVideo(fullUrl)) {
            return (
              <video
                key={`${url}-${index}`}
                src={fullUrl}
                controls
                style={{ width: 180, height: 120, borderRadius: 8, background: '#000', objectFit: 'cover' }}
              />
            );
          }

          return (
            <Image
              key={`${url}-${index}`}
              src={fullUrl}
              width={120}
              height={120}
              style={{ borderRadius: 8, objectFit: 'cover' }}
              alt={`附件${index + 1}`}
            />
          );
        })}
      </Space>
    );
  };

  const handleAssignSubmit = async (staffId?: string, orderItemIds?: string[], noNeedStaffAccept?: boolean) => {
    if (!currentOrder) {
      return;
    }

    try {
      const values = await assignForm.validateFields(['assignReason']);
      const assignedStaffId = staffId || assignForm.getFieldValue('assignedStaffId');
      if (!assignedStaffId) {
        message.warning('请选择服务人员');
        return;
      }
      setAssignSubmitting(true);
      const updated = await serviceBookingOrderService.assign(currentOrder.id, {
        assignedStaffId,
        assignReason: values.assignReason,
        orderItemIds,
        noNeedStaffAccept,
      });
      message.success('派单成功');
      setAssignVisible(false);
      setCurrentOrder(updated);
      loadOrders(pagination.page, pagination.pageSize, searchForm.getFieldsValue(), activeStatusTab);
    } catch {
      // validation or request error already handled
    } finally {
      setAssignSubmitting(false);
    }
  };

  const handleStaffFilterChange = (key: keyof AssignableStaffFilters, checked: boolean) => {
    const nextFilters = { ...staffFilters, [key]: checked };
    setStaffFilters(nextFilters);
    if (currentOrder) {
      loadStaffOptions(currentOrder.id, 1, staffPagination.pageSize, nextFilters);
    }
  };

  const handleStaffTextFilterChange = (key: 'address' | 'staffName' | 'staffPhone', value: string) => {
    const nextFilters = { ...staffFilters, [key]: value };
    setStaffFilters(nextFilters);
    if (currentOrder) {
      loadStaffOptions(currentOrder.id, 1, staffPagination.pageSize, nextFilters);
    }
  };

  const handleStaffPageChange = (page: number, pageSize: number) => {
    if (currentOrder) {
      loadStaffOptions(currentOrder.id, page, pageSize, staffFilters);
    }
  };

  const handleStaffCardClick = (staff: AssignableStaffListItem) => {
    const dateItems = (staff.assignableDateItems || []).filter(item => item.orderItemId);
    const defaultChecked = getDefaultAssignOrderItemIds(dateItems);
    let selectedOrderItemIds = [...defaultChecked];
    let noNeedStaffAccept = true;
    Modal.confirm({
      title: '确认派单？',
      width: 760,
      content: (
        <Space orientation="vertical" size={12} style={{ width: '100%' }}>
          <div>确定将勾选的子单派给 {staff.name || staff.username || '该服务人员'} 吗？</div>
          <Space>
            <Switch
              size="small"
              defaultChecked
              onChange={checked => {
                noNeedStaffAccept = checked;
              }}
            />
            <span>无需服务人员同意接单</span>
          </Space>
          {dateItems.length ? (
            <Checkbox.Group
              defaultValue={defaultChecked}
              style={{ width: '100%' }}
              onChange={values => {
                selectedOrderItemIds = values.map(value => String(value));
              }}
            >
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', columnGap: 20, rowGap: 10 }}>
                {dateItems.map(item => (
                  <Checkbox key={item.orderItemId} value={item.orderItemId}>
                    <Space size={8} wrap>
                      <span>{item.label || `${item.serviceDate} ${item.serviceTimeDesc || ''}`}</span>
                      {item.hasTimeConflict ? <Tag color="orange">时间冲突</Tag> : <Tag color="green">无冲突</Tag>}
                    </Space>
                  </Checkbox>
                ))}
              </div>
            </Checkbox.Group>
          ) : (
            <span style={{ color: '#999' }}>当前没有可派子单日期</span>
          )}
        </Space>
      ),
      okText: '确认派单',
      cancelText: '取消',
      onOk: () => {
        if (!selectedOrderItemIds.length) {
          message.warning('请选择要派单的日期');
          return Promise.reject();
        }
        assignForm.setFieldsValue({ assignedStaffId: staff.id });
        return handleAssignSubmit(staff.id, selectedOrderItemIds, noNeedStaffAccept);
      },
    });
  };

  const renderAssignableDateTags = (dates?: string[]) => {
    const list = (dates || []).filter(Boolean);
    if (!list.length) {
      return <span style={{ color: '#999' }}>暂无</span>;
    }
    const visibleDates = list.slice(0, 2);
    const hiddenDates = list.slice(2);
    return (
      <Space size={[4, 4]} wrap>
        {visibleDates.map(date => <Tag key={date} color="blue">{date}</Tag>)}
        {hiddenDates.length ? (
          <Tooltip
            title={(
              <Space orientation="vertical" size={2}>
                {list.map(date => <span key={date}>{date}</span>)}
              </Space>
            )}
          >
            <Tag color="blue" style={{ cursor: 'pointer' }}>+{hiddenDates.length}</Tag>
          </Tooltip>
        ) : null}
      </Space>
    );
  };

  const renderStaffCard = (staff: AssignableStaffListItem) => (
    <Card
      key={staff.id}
      size="small"
      hoverable
      onClick={() => handleStaffCardClick(staff)}
      style={{
        cursor: 'pointer',
        opacity: 1,
        borderColor: staff.assignable ? '#1677ff' : '#d9d9d9',
        background: staff.assignable ? '#f8fbff' : '#fafafa',
      }}
      styles={{ body: { padding: 14 } }}
    >
      <div style={{ display: 'flex', height: '100%', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
          <Space align="start" style={{ minWidth: 0 }}>
          <Avatar size={46} src={staff.avatar || undefined} icon={<UserOutlined />}>
            {staff.name?.charAt(0)}
          </Avatar>
          <div style={{ minWidth: 0 }}>
            <Space wrap>
              <strong>{staff.name || staff.username || '-'}</strong>
              {staff.assignable ? <Tag color="blue">可派单</Tag> : <Tag>不满足规则</Tag>}
              {staff.hasRejectedCurrentOrder ? <Tag color="red">已拒绝当前订单</Tag> : null}
              {staff.hasTimedOutCurrentOrder ? <Tag color="orange">已超时当前订单</Tag> : null}
            </Space>
            <div style={{ color: '#666', marginTop: 6 }}>
              {staff.phone || '-'} / {staff.serviceRegion || '-'} / {formatStaffDistance(staff.distanceKm)}
            </div>
            <div style={{ color: '#666', marginTop: 4, wordBreak: 'break-all' }}>
              服务分类：{formatServiceProjectsText(staff.serviceProjectsText)}
            </div>
          </div>
          </Space>
          <Button type={staff.assignable ? 'primary' : 'default'} size="small" loading={assignSubmitting}>
          派单
          </Button>
        </div>
        <Space size={[4, 4]} wrap>
          <Tag color={isApprovedCertification(staff.certificationStatus) ? 'green' : 'default'}>认证</Tag>
          <Tag color={staff.orderMode ? 'green' : 'default'}>接单</Tag>
          <Tag color={staff.categoryMatched ? 'green' : 'default'}>分类</Tag>
          <Tag color={staff.serviceTimeMatched ? 'green' : 'default'}>时间</Tag>
          <Tag color={staff.dispatchDistanceMatched ? 'green' : 'default'}>距离{staff.maxDispatchDistanceKm ? `≤${staff.maxDispatchDistanceKm}km` : ''}</Tag>
          <Tag color={staff.hasTimeConflict ? 'red' : 'green'}>{staff.hasTimeConflict ? '有冲突' : '无冲突'}</Tag>
        </Space>
        <div>
          <span style={{ color: '#666', marginRight: 8 }}>可派日期：</span>
          {renderAssignableDateTags(staff.assignableDates)}
        </div>
      <div style={{ paddingTop: 10, borderTop: '1px solid #f0f0f0' }}>
        <div style={{ fontWeight: 500, marginBottom: 6 }}>同时段订单：{staff.sameTimeOrderCount || 0}</div>
        {staff.sameTimeOrders?.length ? (
          <Space orientation="vertical" size={4} style={{ width: '100%' }}>
            {staff.sameTimeOrders.slice(0, 3).map(item => (
              <div key={`${item.orderItemId}-${item.serviceDate}-${item.serviceTimeDesc}`} style={{ color: '#666' }}>
                {item.serviceDate} {item.serviceTimeDesc} / {item.productName || '-'} / {item.orderNo || item.orderItemNo || '-'} / {item.statusText || item.status || '-'}
              </div>
            ))}
          </Space>
        ) : (
          <span style={{ color: '#999' }}>暂无同时段订单</span>
        )}
      </div>
      </div>
    </Card>
  );

  const handleUnassign = async (record: ServiceBookingOrder) => {
    try {
      setUnassignSubmittingId(record.id);
      const updated = await serviceBookingOrderService.unassign(record.id);
      message.success('取消派单成功');
      if (currentOrder?.id === record.id) {
        setCurrentOrder(updated);
      }
      loadOrders(pagination.page, pagination.pageSize, searchForm.getFieldsValue(), activeStatusTab);
    } catch {
      message.error('取消派单失败');
    } finally {
      setUnassignSubmittingId('');
    }
  };

  const canCancelOrder = (record: ServiceBookingOrder) => (
    !['cancelled', 'done', 'accepted_review', 'serving', 'aftersale', 'refunded'].includes(record.status) &&
    (record.payStatus === 'unpaid' || ['pending', 'waiting', 'checkin_pending'].includes(record.status))
  );

  const handleCancelOrder = async (record: ServiceBookingOrder) => {
    try {
      setCancelSubmittingId(record.id);
      const updated = await serviceBookingOrderService.cancel(record.id, { cancelReason: '后台取消订单' });
      message.success('取消订单成功');
      if (currentOrder?.id === record.id) {
        setCurrentOrder(updated);
      }
      loadOrders(pagination.page, pagination.pageSize, searchForm.getFieldsValue(), activeStatusTab);
    } catch {
      message.error('取消订单失败');
    } finally {
      setCancelSubmittingId('');
    }
  };
  const handleMeituanConfirm = async (record: ServiceBookingOrder) => {
    try {
      setMeituanConfirmSubmittingId(record.id);
      const updated = await serviceBookingOrderService.confirmMeituanOrder(record.id);
      message.success('美团接单成功');
      setMeituanConfirmVisible(false);
      if (currentOrder?.id === record.id) {
        setCurrentOrder(updated);
      }
      loadOrders(pagination.page, pagination.pageSize, searchForm.getFieldsValue(), activeStatusTab);
    } catch {
      message.error('美团接单失败');
    } finally {
      setMeituanConfirmSubmittingId('');
    }
  };

  const openMeituanConfirmModal = (record: ServiceBookingOrder) => {
    setMeituanConfirmOrder(record);
    setMeituanRejectReason('后台手动拒单');
    setMeituanConfirmVisible(true);
  };

  const handleMeituanReject = async () => {
    if (!meituanConfirmOrder) {
      return;
    }
    try {
      setMeituanRejectSubmittingId(meituanConfirmOrder.id);
      const updated = await serviceBookingOrderService.rejectMeituanOrder(meituanConfirmOrder.id, {
        rejectReason: meituanRejectReason,
      });
      message.success('美团拒单成功');
      setMeituanConfirmVisible(false);
      if (currentOrder?.id === meituanConfirmOrder.id) {
        setCurrentOrder(updated);
      }
      loadOrders(pagination.page, pagination.pageSize, searchForm.getFieldsValue(), activeStatusTab);
    } catch {
      message.error('美团拒单失败');
    } finally {
      setMeituanRejectSubmittingId('');
    }
  };

  const shouldShowMeituanConfirm = (record: ServiceBookingOrder) => (
    record.source === 'meituan' &&
    Number(record.acceptedByMerchant || 0) !== 1 &&
    record.payStatus !== 'unpaid' &&
    !['cancelled', 'done', 'accepted_review', 'aftersale', 'refunded'].includes(record.status)
  );

  const isMeituanOrder = (record: ServiceBookingOrder) => {
    const source = String(record.source || '').toLowerCase();
    return source === 'meituan' ||
      source.includes('meituan') ||
      Boolean(record.externalOrderId || record.externalReservationNo) ||
      Number(record.meituanOrderFulfillStatus || 0) > 0;
  };

  const openMeituanQueryModal = (record: ServiceBookingOrder) => {
    setMeituanQueryOrder(record);
    setMeituanPaymentSharesResult(null);
    setMeituanOrderInfoResult(null);
    setMeituanCancelReason('');
    setMeituanCancelType(2);
    setMeituanQueryVisible(true);
  };

  const handleOpenMeituanPaymentShares = async () => {
    if (!meituanQueryOrder) {
      return;
    }
    setMeituanAmountQueryVisible(true);
    setMeituanPaymentSharesLoading(true);
    try {
      const response = await serviceBookingOrderService.queryMeituanPaymentShares(meituanQueryOrder.id);
      setMeituanPaymentSharesResult(response);
    } catch {
      setMeituanPaymentSharesResult(null);
      message.error('美团分摊金额查询失败');
    } finally {
      setMeituanPaymentSharesLoading(false);
    }
  };

  const handleOpenMeituanOrderInfo = async () => {
    if (!meituanQueryOrder) {
      return;
    }
    setMeituanCouponQueryVisible(true);
    setMeituanOrderInfoLoading(true);
    try {
      const response = await serviceBookingOrderService.queryMeituanOrderInfo(meituanQueryOrder.id);
      setMeituanOrderInfoResult(response);
      const receiptCode = getFirstMeituanReceiptCode(response);
      const couponInfo = buildOrderCouponInfoFromMeituanResponse(response);
      if (receiptCode || couponInfo.length > 0) {
        const patch = { receiptCode, couponInfo };
        setOrders(prev => prev.map(item => item.id === meituanQueryOrder.id ? { ...item, ...patch } : item));
        if (currentOrder?.id === meituanQueryOrder.id) {
          setCurrentOrder({ ...currentOrder, ...patch });
        }
        setMeituanQueryOrder({ ...meituanQueryOrder, ...patch });
      }
    } catch {
      setMeituanOrderInfoResult(null);
      message.error('美团订单及券码状态查询失败');
    } finally {
      setMeituanOrderInfoLoading(false);
    }
  };

  const handleOpenMeituanOrderInfoEdit = async () => {
    if (!meituanQueryOrder) {
      return;
    }
    meituanOrderInfoEditForm.setFieldsValue({
      externalOrderId: meituanQueryOrder.externalOrderId || '',
      externalReservationNo: meituanQueryOrder.externalReservationNo || '',
      externalShopId: meituanQueryOrder.externalShopId || '',
    });
    setMeituanOrderInfoEditVisible(true);
    setMeituanOrderInfoEditShopLoading(true);
    try {
      const response = await listMeituanShops({ page: 1, pageSize: 1000, status: '1' });
      setMeituanOrderInfoEditShops(response.data.list || []);
    } catch {
      setMeituanOrderInfoEditShops([]);
      message.error('加载美团门店失败');
    } finally {
      setMeituanOrderInfoEditShopLoading(false);
    }
  };

  const handleSubmitMeituanOrderInfoEdit = async () => {
    if (!meituanQueryOrder) {
      return;
    }
    try {
      const values = await meituanOrderInfoEditForm.validateFields();
      setMeituanOrderInfoEditSubmitting(true);
      const updated = await serviceBookingOrderService.updateMeituanOrderInfo(meituanQueryOrder.id, values);
      setOrders(prev => prev.map(item => item.id === updated.id ? { ...item, ...updated } : item));
      if (currentOrder?.id === updated.id) {
        setCurrentOrder(updated);
      }
      setMeituanQueryOrder(updated);
      setMeituanOrderInfoEditVisible(false);
      message.success('订单信息修改成功');
    } catch (error: any) {
      if (!error?.errorFields) {
        message.error('订单信息修改失败');
      }
    } finally {
      setMeituanOrderInfoEditSubmitting(false);
    }
  };

  const loadMeituanShopReviews = async (page = meituanCommentPagination.page, pageSize = meituanCommentPagination.pageSize) => {
    if (!meituanQueryOrder) {
      return;
    }
    const values = meituanCommentForm.getFieldsValue();
    const range = values.reviewTimeRange;
    if (!range || range.length !== 2) {
      message.warning('请选择评论时间范围');
      return;
    }
    setMeituanCommentLoading(true);
    try {
      const response = await serviceBookingOrderService.queryMeituanShopReviews(meituanQueryOrder.id, {
        star: values.star || 1,
        platform: values.platform || 2,
        offset: page,
        limit: pageSize,
        beginTime: range[0].format('YYYY-MM-DD HH:mm:ss'),
        endTime: range[1].format('YYYY-MM-DD HH:mm:ss'),
      });
      setMeituanCommentResult(response);
      setMeituanCommentLocalFilterEnabled(false);
      setMeituanCommentPagination({ page, pageSize });
    } catch {
      setMeituanCommentResult(null);
      message.error('美团用户评论查询失败');
    } finally {
      setMeituanCommentLoading(false);
    }
  };

  const handleOpenMeituanComments = () => {
    if (!meituanQueryOrder) {
      return;
    }
    meituanCommentForm.setFieldsValue({
      star: 1,
      platform: 2,
      reviewTimeRange: [dayjs().subtract(1, 'month'), dayjs()],
    });
    setMeituanCommentResult(null);
    setMeituanCommentLocalFilterEnabled(false);
    setMeituanCommentPagination({ page: 1, pageSize: 10 });
    setMeituanCommentVisible(true);
  };

  const handleOpenMeituanMerchantModify = () => {
    if (!meituanQueryOrder) {
      return;
    }
    const addressParts = getOrderAddressParts(meituanQueryOrder);
    meituanMerchantModifyForm.setFieldsValue({
      reserveUserName: meituanQueryOrder.contactName || undefined,
      reservePhone: meituanQueryOrder.contactPhone || undefined,
      reserveAddress: addressParts.addressDetail || undefined,
      addressDetail: addressParts.addressDetail || undefined,
      provinceName: addressParts.provinceName || undefined,
      cityName: addressParts.cityName || undefined,
      districtName: addressParts.districtName || undefined,
      bookTimeRange: undefined,
      deliveryUserName: undefined,
      deliveryAddress: undefined,
      deliveryPhone: undefined,
    });
    setMeituanMerchantModifyVisible(true);
  };

  const handleOpenMeituanAddPrice = () => {
    if (!meituanQueryOrder) {
      return;
    }
    meituanAddPriceForm.setFieldsValue({
      addPriceItemId: String(meituanQueryOrder.id),
      addPriceSize: 1,
    });
    setMeituanAddPriceVisible(true);
  };

  const handleMerchantModifyMeituanOrder = async () => {
    if (!meituanQueryOrder) {
      return;
    }
    setMeituanMerchantModifySubmitting(true);
    try {
      const values = await meituanMerchantModifyForm.validateFields();
      const [startTime, endTime] = values.bookTimeRange || [];
      const addressDetail = (values.reserveAddress || '').trim();
      const result = await serviceBookingOrderService.merchantModifyMeituanOrder(meituanQueryOrder.id, {
        reserveUserName: (values.reserveUserName || '').trim(),
        reservePhone: (values.reservePhone || '').trim(),
        reserveAddress: buildFullReserveAddress({ ...values, addressDetail }),
        provinceName: (values.provinceName || '').trim(),
        cityName: (values.cityName || '').trim(),
        districtName: (values.districtName || '').trim(),
        addressDetail,
        bookStartTime: startTime.valueOf(),
        bookEndTime: endTime.valueOf(),
        deliveryUserName: (values.deliveryUserName || '').trim(),
        deliveryAddress: (values.deliveryAddress || '').trim(),
        deliveryPhone: (values.deliveryPhone || '').trim(),
      });
      const updated = result.order;
      message.success('美团商户改约成功');
      setMeituanMerchantModifyVisible(false);
      setMeituanQueryOrder(updated);
      if (currentOrder?.id === updated.id) {
        setCurrentOrder(updated);
      }
      loadOrders(pagination.page, pagination.pageSize, searchForm.getFieldsValue(), activeStatusTab);
    } catch (error: any) {
      if (!error?.errorFields) {
        message.error('美团商户改约失败');
      }
    } finally {
      setMeituanMerchantModifySubmitting(false);
    }
  };

  const handleSubmitMeituanAddPrice = async () => {
    if (!meituanQueryOrder) {
      return;
    }
    try {
      const values = await meituanAddPriceForm.validateFields();
      const addPriceUnitPriceFen = Math.round(Number(values.addPriceUnitPriceYuan || 0) * 100);
      const addPriceSize = Number(values.addPriceSize || 0);

      if (addPriceUnitPriceFen <= 0) {
        message.warning('请填写有效的加价金额');
        return;
      }

      setMeituanAddPriceSubmitting(true);
      const result = await serviceBookingOrderService.addPriceMeituanOrder(meituanQueryOrder.id, {
        operateType: 1,
        addPriceDetails: addPriceUnitPriceFen > 0 ? [{
          id: ((values.addPriceItemId || '').trim() || String(meituanQueryOrder.id)),
          unitPrice: addPriceUnitPriceFen,
          size: addPriceSize || 1,
          operateType: 1,
        }] : [],
      });
      const updated = result.order;
      message.success('美团订单加价成功');
      setMeituanAddPriceVisible(false);
      setMeituanQueryOrder(updated);
      if (currentOrder?.id === updated.id) {
        setCurrentOrder(updated);
      }
      setOrders(prev => prev.map(item => item.id === updated.id ? updated : item));
      loadOrders(pagination.page, pagination.pageSize, searchForm.getFieldsValue(), activeStatusTab);
    } catch (error: any) {
      if (!error?.errorFields) {
        message.error('美团订单加价失败');
      }
    } finally {
      setMeituanAddPriceSubmitting(false);
    }
  };

  const handleOpenMeituanChange = async () => {
    if (!meituanQueryOrder) {
      return;
    }
    setMeituanChangeVisible(true);
    setMeituanChangeLoading(true);
    try {
      const record = await serviceBookingOrderService.getLatestMeituanChange(meituanQueryOrder.id);
      setMeituanChangeRecord(record);
      if (!record) {
        message.info('暂无美团改约记录');
      }
    } catch {
      message.error('加载美团改约记录失败');
    } finally {
      setMeituanChangeLoading(false);
    }
  };

  const handleConfirmMeituanChange = async () => {
    if (!meituanQueryOrder) {
      return;
    }
    setMeituanChangeSubmitting(true);
    try {
      const updated = await serviceBookingOrderService.confirmMeituanChange(meituanQueryOrder.id);
      message.success('同意改约成功');
      setMeituanChangeVisible(false);
      setMeituanQueryOrder(updated);
      if (currentOrder?.id === updated.id) {
        setCurrentOrder(updated);
      }
      loadOrders(pagination.page, pagination.pageSize, searchForm.getFieldsValue(), activeStatusTab);
    } catch {
      message.error('同意改约失败');
    } finally {
      setMeituanChangeSubmitting(false);
    }
  };

  const handleRejectMeituanChange = async () => {
    if (!meituanQueryOrder) {
      return;
    }
    setMeituanChangeSubmitting(true);
    try {
      const updated = await serviceBookingOrderService.rejectMeituanChange(meituanQueryOrder.id);
      message.success('拒绝改约成功');
      setMeituanChangeVisible(false);
      setMeituanQueryOrder(updated);
      if (currentOrder?.id === updated.id) {
        setCurrentOrder(updated);
      }
      loadOrders(pagination.page, pagination.pageSize, searchForm.getFieldsValue(), activeStatusTab);
    } catch {
      message.error('拒绝改约失败');
    } finally {
      setMeituanChangeSubmitting(false);
    }
  };

  const handleOpenMeituanCancel = () => {
    setMeituanCancelReason('');
    setMeituanCancelType(2);
    setMeituanCancelVisible(true);
  };

  const handleMerchantCancelMeituanOrder = async () => {
    if (!meituanQueryOrder) {
      return;
    }
    const reason = meituanCancelReason.trim();
    if (!reason) {
      message.warning('请输入取消原因');
      return;
    }
    setMeituanCancelSubmitting(true);
    try {
      const result = await serviceBookingOrderService.merchantCancelMeituanOrder(meituanQueryOrder.id, {
        cancelReason: reason,
        type: meituanCancelType,
      });
      const updated = result.order;
      message.success('美团取消订单成功');
      setMeituanCancelVisible(false);
      setMeituanQueryOrder(updated);
      if (currentOrder?.id === updated.id) {
        setCurrentOrder(updated);
      }
      loadOrders(pagination.page, pagination.pageSize, searchForm.getFieldsValue(), activeStatusTab);
    } catch {
      message.error('美团取消订单失败');
    } finally {
      setMeituanCancelSubmitting(false);
    }
  };

  const columns: ColumnsType<ServiceBookingOrder> = [
    { title: '订单号', dataIndex: 'orderNo', key: 'orderNo', width: 180 },
    {
      title: '订单来源',
      dataIndex: 'source',
      key: 'source',
      width: 100,
      render: (value: string) => renderStatusTag(value, orderSourceMap),
    },
    {
      title: '创建方式',
      dataIndex: 'creationMode',
      key: 'creationMode',
      width: 130,
      render: (value: string) => renderStatusTag(value, creationModeMap),
    },
    {
      title: '添加人',
      dataIndex: 'createdByUserName',
      key: 'createdByUserName',
      width: 120,
      render: (value: string, record) => record.creationMode === 'admin_manual' ? (value || record.createdByUserId || '--') : '--',
    },
    {
      title: '美团预约单号',
      dataIndex: 'externalOrderId',
      key: 'externalOrderId',
      width: 170,
      render: (value: string, record) => isMeituanOrder(record) ? (value || '--') : '--',
    },
    {
      title: '美团预约订单号',
      dataIndex: 'externalReservationNo',
      key: 'externalReservationNo',
      width: 180,
      render: (value: string, record) => isMeituanOrder(record) ? (value || '--') : '--',
    },
    {
      title: '券码信息',
      dataIndex: 'couponInfo',
      key: 'couponInfo',
      width: 220,
      render: (_value, record) => isMeituanOrder(record) ? renderOrderCouponInfo(record) : '--',
    },
    { title: '服务产品', dataIndex: 'productName', key: 'productName', width: 160 },
    {
      title: '宠物数量',
      dataIndex: 'petQuantity',
      key: 'petQuantity',
      width: 100,
      render: (value: number) => `${Number(value || 1)}只`,
    },
    {
      title: '服务时间',
      key: 'serviceTime',
      width: 220,
      render: (_, record) => `${record.serviceDateStart || '-'} 至 ${record.serviceDateEnd || '-'} ${record.serviceTimeDesc || ''}`.trim(),
    },
    {
      title: '服务天数',
      dataIndex: 'totalDays',
      key: 'totalDays',
      width: 100,
      render: (value, record) => {
        const count = Number(value || record.items?.length || 0);
        return count > 0 ? `${count}天` : '-';
      },
    },
    { title: '联系人', dataIndex: 'contactName', key: 'contactName', width: 110 },
    { title: '联系电话', dataIndex: 'contactPhone', key: 'contactPhone', width: 140 },
    {
      title: '服务地址',
      dataIndex: 'addressText',
      key: 'addressText',
      width: 300,
      render: (value: string) => value || '-',
    },
    {
      title: '订单金额',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      width: 110,
      render: (value: number) => `¥${value?.toFixed(2) || '0.00'}`,
    },
    {
      title: '商家营销费',
      dataIndex: 'meituanMerchantMarketingAmount',
      key: 'meituanMerchantMarketingAmount',
      width: 120,
      render: (value: number, record) => isMeituanOrder(record)
        ? `¥${Number(value || 0).toFixed(2)}`
        : '--',
    },
    {
      title: '补价金额',
      dataIndex: 'meituanAddPriceAmount',
      key: 'meituanAddPriceAmount',
      width: 110,
      render: (value: number, record) => isMeituanOrder(record)
        ? (Number(value || 0) > 0 ? `¥${Number(value || 0).toFixed(2)}` : '--')
        : '--',
    },
    {
      title: '补价支付',
      dataIndex: 'meituanAddPricePaid',
      key: 'meituanAddPricePaid',
      width: 100,
      render: (value: number, record) => isMeituanOrder(record) ? renderMeituanAddPricePaidTag(value) : '--',
    },
    {
      title: '支付状态',
      dataIndex: 'payStatus',
      key: 'payStatus',
      width: 100,
      render: (value: string) => renderStatusTag(value, payStatusMap),
    },
    {
      title: '补价成功时间',
      dataIndex: 'meituanAddPriceSuccessTime',
      key: 'meituanAddPriceSuccessTime',
      width: 168,
      render: (value: string, record) => isMeituanOrder(record) ? (value || '--') : '--',
    },
    {
      title: '补价券码',
      dataIndex: 'meituanAddPriceSerialNumber',
      key: 'meituanAddPriceSerialNumber',
      width: 140,
      render: (value: string, record) => isMeituanOrder(record) ? (value || '--') : '--',
    },
    {
      title: '订单状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (_: string, record) => renderOrderStatusTag(record),
    },
    {
      title: '完成履约时间',
      dataIndex: 'fulfillmentCompletedAt',
      key: 'fulfillmentCompletedAt',
      width: 170,
      render: (value: string) => value || '-',
    },
    {
      title: '验券时间',
      dataIndex: 'meituanVerifiedAt',
      key: 'meituanVerifiedAt',
      width: 170,
      render: (value: string, record) => isMeituanOrder(record) ? (value || '-') : '-',
    },
    {
      title: '服务人员',
      dataIndex: 'assignedStaffName',
      key: 'assignedStaffName',
      width: 120,
      render: (value: string) => value || '-',
    },
    {
      title: '代理商名字',
      dataIndex: 'serviceProviderName',
      key: 'serviceProviderName',
      width: 130,
      render: (value: string, record) => value || record.serviceProviderId || '-',
    },
    { title: '创建时间', dataIndex: 'createdAt', key: 'createdAt', width: 160 },
    {
      title: '操作',
      key: 'action',
      width: 300,
      fixed: 'right',
      onHeaderCell: () => ({ style: { width: 300, minWidth: 300, maxWidth: 300 } }),
      onCell: () => ({ style: { width: 300, minWidth: 300, maxWidth: 300, whiteSpace: 'normal' } }),
      render: (_, record) => (
        <Space size="small" wrap style={{ width: '100%' }}>
          <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => handleViewDetail(record)}>
            详情
          </Button>
          {isMeituanOrder(record) ? (
            <Button type="link" size="small" onClick={() => openMeituanQueryModal(record)}>
              美团数据查询
            </Button>
          ) : null}
          {shouldShowMeituanConfirm(record) ? (
            <Button
              type="link"
              size="small"
              loading={meituanConfirmSubmittingId === record.id}
              onClick={() => openMeituanConfirmModal(record)}
            >
              接单
            </Button>
          ) : null}
          {record.payStatus !== 'unpaid' &&
          !shouldShowMeituanConfirm(record) &&
          !['cancelled', 'done', 'accepted_review', 'aftersale', 'refunded'].includes(record.status) ? (
            <Button type="link" size="small" icon={<TeamOutlined />} onClick={() => openAssignModal(record)}>
              {record.status === 'pending' ? '派单' : '改派'}
            </Button>
          ) : null}
          {record.status === 'assigned' || record.status === 'waiting' || record.status === 'checkin_pending' ? (
            <Popconfirm
              title="确认取消派单？"
              description="取消后订单会恢复为待派单，并通知当前服务人员。"
              okText="确认"
              cancelText="取消"
              onConfirm={() => handleUnassign(record)}
            >
              <Button danger type="link" size="small" loading={unassignSubmittingId === record.id}>
                取消派单
              </Button>
            </Popconfirm>
          ) : null}
          {canCancelOrder(record) ? (
            <Popconfirm
              title="确认取消订单？"
              description="取消后主订单将变为已取消，未完成子订单也会同步变为已取消。"
              okText="确认"
              cancelText="取消"
              onConfirm={() => handleCancelOrder(record)}
            >
              <Button danger type="link" size="small" icon={<StopOutlined />} loading={cancelSubmittingId === record.id}>
                取消订单
              </Button>
            </Popconfirm>
          ) : null}
        </Space>
      ),
    },
  ];

  const visibleColumns = columns.filter(
    (column) => !hiddenOrderColumnKeys.has(
      String(column.key || ('dataIndex' in column ? column.dataIndex : '') || ''),
    ),
  );

  const itemColumns: ColumnsType<ServiceBookingOrderSubItem> = [
    { title: '服务日期单号', dataIndex: 'orderNo', key: 'orderNo', width: 180 },
    { title: '服务日期', dataIndex: 'serviceDate', key: 'serviceDate', width: 120 },
    { title: '服务时段', dataIndex: 'serviceTimeDesc', key: 'serviceTimeDesc', width: 120 },
    {
      title: '宠物数量',
      dataIndex: 'petQuantity',
      key: 'petQuantity',
      width: 100,
      render: (value: number) => `${Number(value || 1)}只`,
    },
    {
      title: '金额',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      width: 100,
      render: (value: number) => `¥${value?.toFixed(2) || '0.00'}`,
    },
    {
      title: '服务状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (value: string) => renderStatusTag(value, orderStatusMap),
    },
    {
      title: '服务人员',
      dataIndex: 'assignedStaffName',
      key: 'assignedStaffName',
      width: 120,
      render: (value: string) => value || '-',
    },
    {
      title: '操作',
      key: 'actions',
      width: 220,
      fixed: 'right',
      render: (_: unknown, record: ServiceBookingOrderSubItem) => (
        <Space size={0}>
          <Button type="link" size="small" onClick={() => openServiceFlowModal(record)}>
            查看服务流程
          </Button>
          <Button type="link" size="small" onClick={() => void openSettlementModal(record)}>
            结算流水
          </Button>
          <Button type="link" size="small" danger onClick={() => openAfterSaleDeductionModal(record)}>
            售后扣费
          </Button>
        </Space>
      ),
    },
  ];

  const currentMeituanOrderNo = (meituanQueryOrder?.externalReservationNo || '').trim();
  const meituanCommentList = meituanCommentResult?.list || [];
  const filteredMeituanCommentList = meituanCommentLocalFilterEnabled && currentMeituanOrderNo
    ? meituanCommentList.filter(item => String(item.consumeOrderId || '').trim() === currentMeituanOrderNo)
    : meituanCommentList;
  const meituanOrderReceiptList = meituanOrderInfoResult?.receiptList || [];
  const meituanOrderAmountShareList = meituanOrderReceiptList.find(item => Array.isArray(item.amountShareList) && item.amountShareList.length > 0)?.amountShareList || [];

  return (
    <Card
      title={<><ShoppingCartOutlined /> 订单管理</>}
      extra={
        !user?.isProvider ? (
          <Space>
            <Button onClick={() => setMeituanRecoverVisible(true)}>
              美团预约恢复
            </Button>
            <Button type="primary" onClick={handleOpenCreateModal}>
              添加订单
            </Button>
          </Space>
        ) : null
      }
    >
      <Form form={searchForm} layout="inline" style={{ marginBottom: 16 }} onFinish={handleSearch}>
        <Form.Item name="orderNo">
          <Input placeholder="订单号" allowClear style={{ width: 180 }} />
        </Form.Item>
        <Form.Item name="externalOrderId">
          <Input placeholder="美团单号" allowClear style={{ width: 180 }} />
        </Form.Item>
        <Form.Item name="externalReservationNo">
          <Input placeholder="美团预约订单号" allowClear style={{ width: 180 }} />
        </Form.Item>
        <Form.Item name="couponCode">
          <Input placeholder="券码" allowClear style={{ width: 180 }} />
        </Form.Item>
        <Form.Item name="contactName">
          <Input placeholder="联系人" allowClear style={{ width: 120 }} />
        </Form.Item>
        <Form.Item name="contactPhone">
          <Input placeholder="联系电话" allowClear style={{ width: 140 }} />
        </Form.Item>
        <Form.Item name="address">
          <Input placeholder="服务地址" allowClear style={{ width: 180 }} />
        </Form.Item>
        <Form.Item name="staffName">
          <Input placeholder="服务人员姓名" allowClear style={{ width: 140 }} />
        </Form.Item>
        <Form.Item name="staffPhone">
          <Input placeholder="服务人员手机号" allowClear style={{ width: 150 }} />
        </Form.Item>
        <Form.Item name="serviceDate">
          <DatePicker placeholder="服务日期" allowClear style={{ width: 140 }} />
        </Form.Item>
        <Form.Item name="createdAt">
          <RangePicker placeholder={['下单开始日期', '下单结束日期']} allowClear style={{ width: 260 }} />
        </Form.Item>
        <Form.Item name="completedAt">
          <RangePicker placeholder={['履约完成开始日期', '履约完成结束日期']} allowClear style={{ width: 300 }} />
        </Form.Item>
        {!user?.isProvider ? (
          <Form.Item name="serviceProviderName">
            <Input placeholder="代理商" allowClear style={{ width: 140 }} />
          </Form.Item>
        ) : null}
        <Form.Item name="payStatus">
          <Select placeholder="支付状态" allowClear style={{ width: 120 }}>
            <Option value="unpaid">待支付</Option>
            <Option value="paid">已支付</Option>
          </Select>
        </Form.Item>
        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>
              搜索
            </Button>
            <Button onClick={handleReset}>重置</Button>
          </Space>
        </Form.Item>
      </Form>

      <Tabs activeKey={activeStatusTab} onChange={handleStatusTabChange} items={orderStatusTabs} />

      <div ref={tableWrapRef}>
        <Table
          rowKey="id"
          loading={loading}
          dataSource={orders}
          columns={visibleColumns}
          scroll={{ x: 2100 }}
          pagination={{
            current: pagination.page,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条`,
            onChange: (page, pageSize) => loadOrders(page, pageSize, searchForm.getFieldsValue(), activeStatusTab),
          }}
        />
      </div>

      <Modal
        title="订单详情"
        open={detailVisible}
        onCancel={() => setDetailVisible(false)}
        footer={null}
        width="min(1280px, calc(100vw - 96px))"
        destroyOnHidden
      >
        {!currentOrder ? null : (
          <Space orientation="vertical" size={16} style={{ width: '100%' }}>
            <Card loading={detailLoading} size="small" title="基础信息">
              <Descriptions column={2} size="small">
                <Descriptions.Item label="订单号">{currentOrder.orderNo}</Descriptions.Item>
                <Descriptions.Item label="订单来源">{renderStatusTag(currentOrder.source, orderSourceMap)}</Descriptions.Item>
                <Descriptions.Item label="创建方式">{renderStatusTag(currentOrder.creationMode, creationModeMap)}</Descriptions.Item>
                <Descriptions.Item label="添加人">{currentOrder.creationMode === 'admin_manual' ? (currentOrder.createdByUserName || currentOrder.createdByUserId || '--') : '--'}</Descriptions.Item>
                {isMeituanOrder(currentOrder) ? (
                  <>
                    <Descriptions.Item label="美团订单号">{currentOrder.externalOrderId || '--'}</Descriptions.Item>
                    <Descriptions.Item label="美团预约订单号">{currentOrder.externalReservationNo || '--'}</Descriptions.Item>
                    <Descriptions.Item label="券码">{currentOrder.receiptCode || '--'}</Descriptions.Item>
                  </>
                ) : null}
                <Descriptions.Item label="服务产品">{currentOrder.productName || '-'}</Descriptions.Item>
                <Descriptions.Item label="联系人">{currentOrder.contactName || '-'}</Descriptions.Item>
                <Descriptions.Item label="联系电话">{currentOrder.contactPhone || '-'}</Descriptions.Item>
                <Descriptions.Item label="宠物名称">{currentOrder.petName || '-'}</Descriptions.Item>
                <Descriptions.Item label="宠物数量">{Number(currentOrder.petQuantity || 1)}只</Descriptions.Item>
                <Descriptions.Item label="服务时段">{currentOrder.serviceTimeDesc || '-'}</Descriptions.Item>
                <Descriptions.Item label="服务日期">
                  {currentOrder.serviceDateStart || '-'} 至 {currentOrder.serviceDateEnd || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="服务天数">{currentOrder.totalDays || 0}</Descriptions.Item>
                <Descriptions.Item label="支付状态">
                  {renderStatusTag(currentOrder.payStatus, payStatusMap)}
                </Descriptions.Item>
                <Descriptions.Item label="订单状态">
                  {renderOrderStatusTag(currentOrder)}
                </Descriptions.Item>
                <Descriptions.Item label="服务人员">{currentOrder.assignedStaffName || '-'}</Descriptions.Item>
                <Descriptions.Item label="代理商名字">{currentOrder.serviceProviderName || '--'}</Descriptions.Item>
                <Descriptions.Item label="订单金额">¥{currentOrder.totalAmount?.toFixed(2) || '0.00'}</Descriptions.Item>
                <Descriptions.Item label="支付时间">
                  <Space size={8}>
                    <span>{currentOrder.paidAt || '-'}</span>
                    {canManageOrderCorrections && currentOrder.creationMode === 'admin_manual' ? (
                      <Button type="link" size="small" icon={<EditOutlined />} onClick={openPaymentTimeCorrection}>
                        更正
                      </Button>
                    ) : null}
                  </Space>
                </Descriptions.Item>
                {canManageOrderCorrections && currentOrder.creationMode === 'admin_manual' && currentOrder.source !== 'meituan' && currentOrder.status === 'pending' && !currentOrder.assignedStaffId ? (
                  <Descriptions.Item label="订单更正">
                    <Button type="link" size="small" icon={<EditOutlined />} onClick={openBasicInfoCorrection}>
                      更正基础信息
                    </Button>
                  </Descriptions.Item>
                ) : null}
                <Descriptions.Item label="服务地址" span={2}>{currentOrder.addressText || '-'}</Descriptions.Item>
                <Descriptions.Item label="备注" span={2}>{currentOrder.remark || '-'}</Descriptions.Item>
              </Descriptions>
            </Card>

            <Card size="small" title="服务日期列表">
              <Table
                rowKey="id"
                size="small"
                pagination={false}
                scroll={{ x: 1100 }}
                dataSource={currentOrder.items || []}
                columns={itemColumns}
              />
            </Card>
          </Space>
        )}
      </Modal>

      <Modal
        title="更正订单基础信息"
        open={basicInfoCorrectionVisible}
        onCancel={() => setBasicInfoCorrectionVisible(false)}
        onOk={() => void handleBasicInfoCorrection()}
        confirmLoading={basicInfoCorrectionSubmitting}
        width={760}
        destroyOnHidden
      >
        <Form form={basicInfoCorrectionForm} layout="vertical">
          <Space size={16} style={{ width: '100%' }} align="start">
            <Form.Item name="contactName" label="联系人" rules={[{ required: true, whitespace: true, message: '请输入联系人' }]} style={{ flex: 1 }}>
              <Input />
            </Form.Item>
            <Form.Item name="contactPhone" label="联系电话" rules={[{ required: true, whitespace: true, message: '请输入联系电话' }]} style={{ flex: 1 }}>
              <Input />
            </Form.Item>
          </Space>
          <Space size={16} style={{ width: '100%' }} align="start">
            <Form.Item name="provinceName" label="省" style={{ flex: 1 }}><Input /></Form.Item>
            <Form.Item name="cityName" label="市" style={{ flex: 1 }}><Input /></Form.Item>
            <Form.Item name="districtName" label="区/县" style={{ flex: 1 }}><Input /></Form.Item>
          </Space>
          <Form.Item name="addressDetail" label="详细地址" rules={[{ required: true, whitespace: true, message: '请输入详细地址' }]}>
            <Input.TextArea rows={2} />
          </Form.Item>
          <Space size={16} style={{ width: '100%' }} align="start">
            <Form.Item name="serviceStartAt" label="服务开始时间" rules={[{ required: true, message: '请选择服务开始时间' }]} style={{ flex: 1 }}>
              <DatePicker showTime={{ format: 'HH:mm' }} format="YYYY-MM-DD HH:mm" style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="serviceEndAt" label="服务结束时间" style={{ flex: 1 }} extra="不填默认加30分钟">
              <DatePicker showTime={{ format: 'HH:mm' }} format="YYYY-MM-DD HH:mm" style={{ width: '100%' }} />
            </Form.Item>
          </Space>
          <Form.Item name="remark" label="备注"><Input.TextArea rows={2} maxLength={500} showCount /></Form.Item>
          <Form.Item name="reason" label="更正原因" rules={[{ required: true, whitespace: true, message: '请填写更正原因' }]}>
            <Input.TextArea rows={3} maxLength={500} showCount />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="更正支付时间"
        open={paymentTimeCorrectionVisible}
        onCancel={() => setPaymentTimeCorrectionVisible(false)}
        onOk={() => void handlePaymentTimeCorrection()}
        confirmLoading={paymentTimeCorrectionSubmitting}
        destroyOnHidden
      >
        <Form form={paymentTimeCorrectionForm} layout="vertical">
          <Form.Item name="paidAt" label="支付时间" rules={[{ required: true, message: '请选择支付时间' }]}>
            <DatePicker
              showTime={{ format: 'HH:mm:ss' }}
              format="YYYY-MM-DD HH:mm:ss"
              style={{ width: '100%' }}
            />
          </Form.Item>
          <Form.Item
            name="reason"
            label="更正原因"
            rules={[{ required: true, whitespace: true, message: '请填写更正原因' }]}
          >
            <Input.TextArea rows={3} maxLength={500} showCount placeholder="请输入更正原因" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="服务流程"
        open={serviceFlowVisible}
        onCancel={() => setServiceFlowVisible(false)}
        footer={null}
        width={860}
        destroyOnHidden
      >
        {!currentServiceFlowItem ? null : (
          <Space orientation="vertical" size={16} style={{ width: '100%' }}>
            <Descriptions column={2} size="small" bordered>
              <Descriptions.Item label="服务日期">{currentServiceFlowItem.serviceDate || '-'}</Descriptions.Item>
              <Descriptions.Item label="服务时段">{currentServiceFlowItem.serviceTimeDesc || '-'}</Descriptions.Item>
              <Descriptions.Item label="服务人员">{currentServiceFlowItem.assignedStaffName || '-'}</Descriptions.Item>
              <Descriptions.Item label="联系电话">{currentServiceFlowItem.assignedStaffPhone || '-'}</Descriptions.Item>
              <Descriptions.Item label="服务状态">
                {renderStatusTag(currentServiceFlowItem.status, orderStatusMap)}
              </Descriptions.Item>
              <Descriptions.Item label="完成时间">{currentServiceFlowItem.completedAt || '-'}</Descriptions.Item>
            </Descriptions>

            <Card size="small" title="签到信息和服务中流程">
              {(currentServiceFlowItem.serviceRecords || []).length ? (
                <Timeline
                  items={(currentServiceFlowItem.serviceRecords || []).map((record, index) => ({
                    key: `${record.occurredAt || index}-${record.title || record.processStepName || index}`,
                    color: record.title?.includes('完成') ? 'green' : record.title?.includes('签到') ? 'blue' : 'gray',
                    children: (
                      <div>
                        <div style={{ fontWeight: 600 }}>
                          {record.processStepName || record.title || `流程记录${index + 1}`}
                        </div>
                        <div style={{ color: '#666', marginTop: 4 }}>
                          {record.occurredAt || '-'} {record.operator ? `｜${record.operator}` : ''}
                        </div>
                        {record.desc ? <div style={{ marginTop: 6 }}>{record.desc}</div> : null}
                        {record.remark ? <div style={{ marginTop: 6 }}>备注：{record.remark}</div> : null}
                        {renderServiceFlowMedia(record.images)}
                      </div>
                    ),
                  }))}
                />
              ) : (
                <Empty description="暂无签到或服务流程记录" />
              )}
            </Card>

            {(currentServiceFlowItem.proofMedia || []).length ? (
              <Card size="small" title="服务凭证">
                {renderServiceFlowMedia(currentServiceFlowItem.proofMedia)}
              </Card>
            ) : null}
          </Space>
        )}
      </Modal>

      <Modal
        title={`结算流水${currentSettlementItem?.orderNo ? ` - ${currentSettlementItem.orderNo}` : ''}`}
        open={settlementVisible}
        onCancel={() => {
          setSettlementVisible(false);
          setCurrentSettlementItem(null);
          setSubOrderSettlements([]);
          setCurrentSettlementRecord(null);
        }}
        footer={
          <Space>
            {canEditSettlement ? (
              <Button onClick={openSettlementEditModal}>
                编辑
              </Button>
            ) : null}
            {canGenerateSettlement ? (
              <Button
                type="primary"
                onClick={() => void handleGenerateSettlement()}
                loading={settlementGenerating}
              >
                生成流水
              </Button>
            ) : null}
            {canSettleSettlement ? (
              <Button
                type="primary"
                onClick={() => void handleSettleSettlement()}
                loading={settlementSubmitting}
              >
                结算
              </Button>
            ) : null}
            <Button
              onClick={() => {
                setSettlementVisible(false);
                setCurrentSettlementItem(null);
                setSubOrderSettlements([]);
                setCurrentSettlementRecord(null);
              }}
            >
              关闭
            </Button>
          </Space>
        }
        width={1280}
        destroyOnHidden
      >
        <Card loading={settlementLoading} size="small" variant="borderless">
          {subOrderSettlements.length === 0 ? (
            <div style={{ padding: '48px 0', textAlign: 'center', color: '#999' }}>暂无结算流水</div>
          ) : (
            <Table<SubOrderSettlementListItem>
              rowKey="id"
              size="small"
              pagination={false}
              dataSource={subOrderSettlements}
              rowSelection={{
                type: 'radio',
                selectedRowKeys: currentSettlementRecord ? [currentSettlementRecord.id] : [],
                onChange: (_keys, rows) => setCurrentSettlementRecord(rows[0] || null),
              }}
              columns={[
                {
                  title: '流水类型',
                  dataIndex: 'flowType',
                  width: 120,
                  render: (value: string) => value === 'after_sale_deduction' ? <Tag color="error">售后扣费</Tag> : <Tag color="blue">正常结算</Tag>,
                },
                {
                  title: '状态',
                  dataIndex: 'status',
                  width: 100,
                  render: (value: string) => renderStatusTag(value, {
                    pending: { color: 'default', text: '待结算' },
                    settled: { color: 'success', text: '已结算' },
                  }),
                },
                {
                  title: '结算金额',
                  dataIndex: 'settlementAmount',
                  width: 120,
                  render: (value: number) => (
                    <span style={{ color: Number(value || 0) < 0 ? '#cf1322' : undefined }}>
                      ¥{Number(value || 0).toFixed(2)}
                    </span>
                  ),
                },
                { title: '备注', dataIndex: 'remark', render: (value: string) => value || '-' },
                { title: '完成时间', dataIndex: 'completedAt', width: 170, render: (value: string) => value || '-' },
                { title: '可结算时间', dataIndex: 'eligibleSettleAt', width: 170, render: (value: string) => value || '-' },
                { title: '结算时间', dataIndex: 'settledAt', width: 170, render: (value: string) => value || '-' },
                { title: '创建时间', dataIndex: 'createdAt', width: 170, render: (value: string) => value || '-' },
              ]}
            />
          )}
        </Card>
      </Modal>

      <Modal
        title="添加订单"
        open={createVisible}
        onCancel={() => setCreateVisible(false)}
        onOk={handleCreateOrder}
        okText="确认添加"
        cancelText="取消"
        confirmLoading={createSubmitting}
        width={1080}
        destroyOnHidden
      >
        <Form form={createForm} layout="vertical">
          <Form.Item label="订单来源" required>
            <Form.Item name="source" rules={[{ required: true, message: '请选择订单来源' }]} hidden>
              <Input />
            </Form.Item>
            <Space size={16} style={{ width: '100%' }} wrap>
              {createOrderSourceOptions.map(option => {
                const active = (createForm.getFieldValue('source') || 'wechat') === option.value;
                return (
                  <div
                    key={option.value}
                    onClick={() => handleCreateSourceChange(option.value)}
                    style={{
                      flex: '1 1 240px',
                      minWidth: 240,
                      border: active ? '1px solid #1677ff' : '1px solid #d9d9d9',
                      borderRadius: 12,
                      padding: '14px 16px',
                      cursor: 'pointer',
                      background: active ? '#f0f7ff' : '#fff',
                      boxShadow: active ? '0 0 0 2px rgba(22,119,255,0.08)' : 'none',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <Space size={12} align="start">
                      <div
                        style={{
                          width: 40,
                          height: 40,
                          borderRadius: 10,
                          background: active ? '#e6f4ff' : '#f5f5f5',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        {option.icon}
                      </div>
                      <div>
                        <div style={{ fontSize: 16, fontWeight: 600, color: '#1f1f1f', marginBottom: 4 }}>
                          {option.title}
                        </div>
                        <div style={{ fontSize: 13, color: '#8c8c8c', lineHeight: 1.5 }}>
                          {option.description}
                        </div>
                      </div>
                    </Space>
                  </div>
                );
              })}
            </Space>
          </Form.Item>

          <Space size={20} style={{ width: '100%' }} align="start">
            <Form.Item name="categoryId" label="服务分类" rules={[{ required: true, message: '请选择服务分类' }]} style={{ flex: 1.2 }}>
              <Select
                showSearch
                loading={createCategoryLoading}
                placeholder="请选择服务分类"
                optionFilterProp="label"
                style={{ width: '100%' }}
                onChange={handleCreateCategoryChange}
                options={createCategories.map(item => ({
                  label: item.displayName,
                  value: item.id,
                }))}
              />
            </Form.Item>
            <Form.Item name="productId" label="服务产品" rules={[{ required: true, message: '请选择服务产品' }]} style={{ flex: 1.6 }}>
              <Select
                showSearch
                loading={createProductLoading}
                placeholder="请选择服务产品"
                optionFilterProp="label"
                style={{ width: '100%' }}
                onChange={handleCreateProductChange}
                options={createProducts.map(item => ({
                  label: item.name,
                  value: item.id,
                }))}
              />
            </Form.Item>
          </Space>
          <Form.Item name="categoryName" hidden>
            <Input />
          </Form.Item>
          <Form.Item name="productName" hidden>
            <Input />
          </Form.Item>

          <Form.Item noStyle shouldUpdate={(prev, next) => prev.source !== next.source}>
            {({ getFieldValue }) => (
              getFieldValue('source') === 'meituan' ? (
                <>
                  <Space size={20} style={{ width: '100%' }} align="start">
                    <Form.Item
                      name="externalReservationNo"
                      label="预约单号"
                      style={{ flex: 1.1 }}
                    >
                      <Input placeholder="请输入预约单号" />
                    </Form.Item>
                    <Form.Item
                      name="externalShopId"
                      label="美团门店"
                      rules={[{ required: true, message: '请选择美团门店' }]}
                      style={{ flex: 2 }}
                    >
                      <Select
                        showSearch
                        loading={createMeituanShopLoading}
                        placeholder="请选择美团门店"
                        optionFilterProp="label"
                        style={{ width: '100%' }}
                        popupMatchSelectWidth={520}
                        onChange={handleCreateMeituanShopChange}
                        options={createMeituanShops.map(shop => ({
                          label: `${shop.shopName || shop.opBizName || shop.shopId}`,
                          value: shop.shopId,
                        }))}
                        notFoundContent={createMeituanShopLoading ? '加载中...' : '暂无可选门店'}
                        optionRender={(option) => (
                          <div title={String(option.label || '')} style={{ whiteSpace: 'normal', lineHeight: 1.5 }}>
                            {option.label}
                          </div>
                        )}
                      />
                    </Form.Item>
                  </Space>
                  <Space size={16} style={{ width: '100%' }} align="start">
                    <Form.Item
                      name="externalOrderId"
                      label="美团订单号"
                      rules={[{ required: true, message: '请输入美团订单号' }]}
                      style={{ flex: 1 }}
                    >
                      <Input placeholder="可手填，查询后自动回填" />
                    </Form.Item>
                  </Space>
                </>
              ) : null
            )}
          </Form.Item>

          <Space size={16} style={{ width: '100%' }} align="start">
            <Form.Item name="contactName" label="联系人" rules={[{ required: true, message: '请输入联系人' }]} style={{ flex: 1 }}>
              <Input placeholder="请输入联系人" />
            </Form.Item>
            <Form.Item name="contactPhone" label="联系电话" rules={[{ required: true, message: '请输入联系电话' }]} style={{ flex: 1 }}>
              <Input placeholder="请输入联系电话" />
            </Form.Item>
          </Space>

          <Space size={16} style={{ width: '100%' }} align="start">
            <Form.Item name="provinceName" label="省" style={{ flex: 1 }}>
              <Input placeholder="请输入省" />
            </Form.Item>
            <Form.Item name="cityName" label="市" style={{ flex: 1 }}>
              <Input placeholder="请输入市" />
            </Form.Item>
            <Form.Item name="districtName" label="区/县" style={{ flex: 1 }}>
              <Input placeholder="请输入区/县" />
            </Form.Item>
          </Space>

          <Form.Item name="addressDetail" label="详细地址" rules={[{ required: true, message: '请输入详细地址' }]}>
            <Input.TextArea rows={2} placeholder="请输入详细地址" />
          </Form.Item>

          <Space size={16} style={{ width: '100%' }} align="start">
            <Form.Item name="petQuantity" label="数量" style={{ flex: 1 }}>
              <InputNumber min={1} precision={0} style={{ width: '100%' }} placeholder="默认1" />
            </Form.Item>
          </Space>

          <Space size={16} style={{ width: '100%' }} align="start">
            <Form.Item
              name="serviceStartAt"
              label="服务开始时间"
              rules={[{ required: true, message: '请选择服务开始时间' }]}
              style={{ flex: 1 }}
              extra="请选择服务开始日期和时间"
            >
              <DatePicker
                showTime={{ format: 'HH:mm' }}
                format="YYYY-MM-DD HH:mm"
                placeholder="请选择服务开始时间"
                style={{ width: '100%' }}
              />
            </Form.Item>
            <Form.Item
              name="serviceEndAt"
              label="服务结束时间"
              style={{ flex: 1 }}
              extra="不填默认加30分钟"
            >
              <DatePicker
                showTime={{ format: 'HH:mm' }}
                format="YYYY-MM-DD HH:mm"
                placeholder="请选择服务结束时间"
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Space>

          <Space size={16} style={{ width: '100%' }} align="start">
            <Form.Item
              name="totalAmount"
              label="订单金额"
              rules={[{ required: true, message: '请输入订单金额' }]}
              style={{ flex: 1 }}
            >
              <InputNumber min={0.01} style={{ width: '100%' }} placeholder="请输入订单金额" />
            </Form.Item>
            <Form.Item name="paidAt" label="支付时间" extra="不填默认使用当前时间" style={{ flex: 1 }}>
              <DatePicker
                showTime={{ format: 'HH:mm:ss' }}
                format="YYYY-MM-DD HH:mm:ss"
                placeholder="请选择支付时间"
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Space>

          <Form.Item name="remark" label="备注">
            <Input.TextArea rows={3} placeholder="请输入备注" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={currentOrder?.status === 'pending' ? '手动派单' : '改派服务人员'}
        open={assignVisible}
        onCancel={() => setAssignVisible(false)}
        footer={null}
        width={1280}
        destroyOnHidden
      >
        <Form form={assignForm} layout="vertical">
          <Form.Item name="assignedStaffId" hidden>
            <Input />
          </Form.Item>
          <Form.Item
            name="assignReason"
            label="派单说明"
            rules={[{ required: true, message: '请输入派单说明' }]}
          >
            <Input.TextArea rows={3} maxLength={200} showCount placeholder="请输入派单或改派说明" />
          </Form.Item>
        </Form>
        <div style={{ marginBottom: 12 }}>
          <Space size={[12, 8]} wrap style={{ marginBottom: 12 }}>
            <span>查询条件：</span>
            <Input
              allowClear
              placeholder="服务人员姓名"
              style={{ width: 160 }}
              value={staffFilters.staffName}
              onChange={(event) => handleStaffTextFilterChange('staffName', event.target.value)}
            />
            <Input
              allowClear
              placeholder="服务人员电话"
              style={{ width: 160 }}
              value={staffFilters.staffPhone}
              onChange={(event) => handleStaffTextFilterChange('staffPhone', event.target.value)}
            />
            <Input
              allowClear
              placeholder="服务地址"
              style={{ width: 220 }}
              value={staffFilters.address}
              onChange={(event) => handleStaffTextFilterChange('address', event.target.value)}
            />
          </Space>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
            <Space size={[12, 8]} wrap>
              <span>筛选条件：</span>
              <Space><Switch size="small" checked={staffFilters.requireCertification} onChange={checked => handleStaffFilterChange('requireCertification', checked)} /><span>认证通过</span></Space>
              <Space><Switch size="small" checked={staffFilters.requireOrderMode} onChange={checked => handleStaffFilterChange('requireOrderMode', checked)} /><span>开启接单</span></Space>
              <Space><Switch size="small" checked={staffFilters.requireServiceCategory} onChange={checked => handleStaffFilterChange('requireServiceCategory', checked)} /><span>服务分类匹配</span></Space>
              <Space><Switch size="small" checked={staffFilters.requireServiceTime} onChange={checked => handleStaffFilterChange('requireServiceTime', checked)} /><span>服务时间匹配</span></Space>
              <Space><Switch size="small" checked={staffFilters.requireDispatchDistance} onChange={checked => handleStaffFilterChange('requireDispatchDistance', checked)} /><span>派单距离内</span></Space>
              <Space><Switch size="small" checked={staffFilters.excludeTimeConflict} onChange={checked => handleStaffFilterChange('excludeTimeConflict', checked)} /><span>排除时间冲突</span></Space>
              <Space><Switch size="small" checked={staffFilters.excludeRejectedCurrentOrder} onChange={checked => handleStaffFilterChange('excludeRejectedCurrentOrder', checked)} /><span>排除已拒绝</span></Space>
              <Space><Switch size="small" checked={staffFilters.excludeTimedOutCurrentOrder} onChange={checked => handleStaffFilterChange('excludeTimedOutCurrentOrder', checked)} /><span>排除已超时</span></Space>
            </Space>
            <span style={{ color: '#666' }}>共 {staffPagination.total} 人，每页 10 人</span>
          </div>
        </div>
        <Spin spinning={staffLoading}>
          {staffOptions.length ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 12 }}>
              {staffOptions.map(renderStaffCard)}
            </div>
          ) : (
            <Empty description={staffLoading ? '正在加载服务人员' : '暂无服务人员'} />
          )}
        </Spin>
        <div style={{ marginTop: 16, textAlign: 'right' }}>
          <Pagination
            current={staffPagination.page}
            pageSize={staffPagination.pageSize}
            total={staffPagination.total}
            showSizeChanger={false}
            onChange={handleStaffPageChange}
          />
        </div>
      </Modal>

      <Modal
        title="美团接单处理"
        open={meituanConfirmVisible}
        onCancel={() => setMeituanConfirmVisible(false)}
        footer={null}
        width={520}
        destroyOnHidden
      >
        <Space orientation="vertical" size={16} style={{ width: '100%' }}>
          <Descriptions column={1} size="small" bordered>
            <Descriptions.Item label="平台订单号">{meituanConfirmOrder?.orderNo || '-'}</Descriptions.Item>
            <Descriptions.Item label="美团订单ID">{meituanConfirmOrder?.externalOrderId || '-'}</Descriptions.Item>
          </Descriptions>
          <Input.TextArea
            rows={3}
            maxLength={200}
            showCount
            value={meituanRejectReason}
            onChange={(event) => setMeituanRejectReason(event.target.value)}
            placeholder="拒单原因，仅拒单时使用"
          />
          <Space>
            <Button
              type="primary"
              loading={meituanConfirmOrder ? meituanConfirmSubmittingId === meituanConfirmOrder.id : false}
              onClick={() => meituanConfirmOrder && handleMeituanConfirm(meituanConfirmOrder)}
            >
              确认接单
            </Button>
            <Button
              danger
              loading={meituanConfirmOrder ? meituanRejectSubmittingId === meituanConfirmOrder.id : false}
              onClick={handleMeituanReject}
            >
              拒单
            </Button>
          </Space>
        </Space>
      </Modal>

      <Modal
        title="美团数据查询"
        open={meituanQueryVisible}
        onCancel={() => setMeituanQueryVisible(false)}
        footer={null}
        width={760}
        destroyOnHidden
      >
        <Space orientation="vertical" size={16} style={{ width: '100%' }}>
          <Descriptions title="订单信息" column={2} size="small" bordered>
            <Descriptions.Item label="平台订单号">{meituanQueryOrder?.orderNo || '-'}</Descriptions.Item>
            <Descriptions.Item label="美团预约单号">{meituanQueryOrder?.externalOrderId || '-'}</Descriptions.Item>
            <Descriptions.Item label="美团订单号">{meituanQueryOrder?.externalReservationNo || '-'}</Descriptions.Item>
            <Descriptions.Item label="美团门店">{meituanQueryOrder?.externalShopName || meituanQueryOrder?.externalShopId || '-'}</Descriptions.Item>
            <Descriptions.Item label="服务产品">{meituanQueryOrder?.productName || '-'}</Descriptions.Item>
            <Descriptions.Item label="宠物数量">{Number(meituanQueryOrder?.petQuantity || 1)}只</Descriptions.Item>
            <Descriptions.Item label="联系人">{meituanQueryOrder?.contactName || '-'}</Descriptions.Item>
            <Descriptions.Item label="联系电话">{meituanQueryOrder?.contactPhone || '-'}</Descriptions.Item>
            <Descriptions.Item label="服务日期">
              {meituanQueryOrder?.serviceDateStart || '-'} 至 {meituanQueryOrder?.serviceDateEnd || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="服务时段">{meituanQueryOrder?.serviceTimeDesc || '-'}</Descriptions.Item>
            <Descriptions.Item label="服务人员">{meituanQueryOrder?.assignedStaffName || '-'}</Descriptions.Item>
            <Descriptions.Item label="服务人员电话">{meituanQueryOrder?.assignedStaffPhone || '-'}</Descriptions.Item>
            <Descriptions.Item label="订单状态">{meituanQueryOrder ? renderOrderStatusTag(meituanQueryOrder) : '-'}</Descriptions.Item>
            <Descriptions.Item label="美团履约">{renderMeituanFulfillStatusTag(meituanQueryOrder?.meituanOrderFulfillStatus)}</Descriptions.Item>
            <Descriptions.Item label="补价金额">
              {Number(meituanQueryOrder?.meituanAddPriceAmount || 0) > 0 ? `¥${Number(meituanQueryOrder?.meituanAddPriceAmount || 0).toFixed(2)}` : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="商家营销费">
              {`¥${Number(meituanQueryOrder?.meituanMerchantMarketingAmount || 0).toFixed(2)}`}
            </Descriptions.Item>
            <Descriptions.Item label="补价支付" span={2}>{renderMeituanAddPricePaidTag(meituanQueryOrder?.meituanAddPricePaid)}</Descriptions.Item>
            <Descriptions.Item label="补价链接" span={2}>
              {meituanQueryOrder?.meituanAddPriceUrl ? (
                <a href={meituanQueryOrder.meituanAddPriceUrl} target="_blank" rel="noreferrer">{meituanQueryOrder.meituanAddPriceUrl}</a>
              ) : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="补价成功时间">
              {meituanQueryOrder?.meituanAddPriceSuccessTime || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="补价券码">
              {meituanQueryOrder?.meituanAddPriceSerialNumber || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="补价透传参数" span={2}>
              {meituanQueryOrder?.meituanAddPriceThirdPartyPaymentInfo || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="服务地址" span={2}>{meituanQueryOrder?.addressText || '-'}</Descriptions.Item>
          </Descriptions>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
            <MeituanQueryActionCard
              title="用户评论"
              desc="查询当前门店的美团/点评用户评价"
              icon={<CommentOutlined />}
              onClick={handleOpenMeituanComments}
            />
            <MeituanQueryActionCard
              title="券码查询"
              desc="查询美团订单券码、核销状态和金额分摊"
              icon={<BarcodeOutlined />}
              onClick={handleOpenMeituanOrderInfo}
            />
            <MeituanQueryActionCard
              title="分摊明细"
              desc="查询美团订单逐券资金分摊明细"
              icon={<DollarCircleOutlined />}
              onClick={handleOpenMeituanPaymentShares}
            />
            <MeituanQueryActionCard
              title="订单信息修改"
              desc="修改美团预约单号、美团订单号和门店"
              icon={<EditOutlined />}
              onClick={handleOpenMeituanOrderInfoEdit}
            />
            <MeituanQueryActionCard
              title="改约确认"
              desc="处理用户发起的美团改约申请"
              icon={<CalendarOutlined />}
              onClick={handleOpenMeituanChange}
            />
            <MeituanQueryActionCard
              title="商户改约"
              desc="商家主动发起美团侧订单改约"
              icon={<CalendarOutlined />}
              onClick={handleOpenMeituanMerchantModify}
            />
            {false ? (
              <MeituanQueryActionCard
                title="订单加价"
                desc="发起美团补价并生成用户支付链接"
                icon={<DollarCircleOutlined />}
                onClick={handleOpenMeituanAddPrice}
              />
            ) : null}
            <MeituanQueryActionCard
              title="美团取消订单"
              desc="商家主动发起美团侧订单取消"
              icon={<StopOutlined />}
              onClick={handleOpenMeituanCancel}
            />
          </div>
        </Space>
      </Modal>

      <Modal
        title="订单信息修改"
        open={meituanOrderInfoEditVisible}
        onCancel={() => setMeituanOrderInfoEditVisible(false)}
        onOk={handleSubmitMeituanOrderInfoEdit}
        okText="确认保存"
        cancelText="关闭"
        confirmLoading={meituanOrderInfoEditSubmitting}
        width={640}
        destroyOnHidden
      >
        <Form form={meituanOrderInfoEditForm} layout="vertical">
          <Form.Item
            name="externalOrderId"
            label="美团预约单号"
            rules={[{ required: true, message: '请输入美团预约单号' }]}
          >
            <Input placeholder="请输入美团预约单号" />
          </Form.Item>
          <Form.Item
            name="externalReservationNo"
            label="美团订单号"
            rules={[{ required: true, message: '请输入美团订单号' }]}
          >
            <Input placeholder="请输入美团订单号" />
          </Form.Item>
          <Form.Item
            name="externalShopId"
            label="美团门店"
            rules={[{ required: true, message: '请选择美团门店' }]}
          >
            <Select
              showSearch
              loading={meituanOrderInfoEditShopLoading}
              placeholder="请选择美团门店"
              optionFilterProp="label"
              options={meituanOrderInfoEditShops.map(shop => ({
                label: `${shop.shopName || shop.opBizName || shop.shopId}`,
                value: shop.shopId,
              }))}
              notFoundContent={meituanOrderInfoEditShopLoading ? '加载中...' : '暂无可选门店'}
              optionRender={(option) => (
                <div title={String(option.label || '')} style={{ whiteSpace: 'normal', lineHeight: 1.5 }}>
                  {option.label}
                </div>
              )}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="美团取消订单"
        open={meituanCancelVisible}
        onCancel={() => setMeituanCancelVisible(false)}
        onOk={handleMerchantCancelMeituanOrder}
        okText="确认取消"
        cancelText="关闭"
        confirmLoading={meituanCancelSubmitting}
        okButtonProps={{ danger: true }}
        width={560}
        destroyOnHidden
      >
        <Space orientation="vertical" size={14} style={{ width: '100%' }}>
          <Descriptions column={1} size="small" bordered>
            <Descriptions.Item label="美团订单ID">{meituanQueryOrder?.externalOrderId || '-'}</Descriptions.Item>
            <Descriptions.Item label="平台订单号">{meituanQueryOrder?.orderNo || '-'}</Descriptions.Item>
            <Descriptions.Item label="当前状态">{meituanQueryOrder ? renderOrderStatusTag(meituanQueryOrder) : '-'}</Descriptions.Item>
          </Descriptions>
          <Form layout="vertical">
            <Form.Item required label="取消原因">
              <Input.TextArea
                value={meituanCancelReason}
                onChange={event => setMeituanCancelReason(event.target.value)}
                placeholder="请输入取消原因，例如：商家无法履约"
                rows={3}
                maxLength={100}
                showCount
              />
            </Form.Item>
            <Form.Item required label="交易类型">
              <Select value={meituanCancelType} onChange={setMeituanCancelType}>
                <Option value={1}>1 - 预订（旧字段）</Option>
                <Option value={2}>2 - 团单（团购后预约）</Option>
                <Option value={3}>3 - 预订（支持核销后取消）</Option>
              </Select>
            </Form.Item>
          </Form>
        </Space>
      </Modal>

      <Modal
        title="商户改约"
        open={meituanMerchantModifyVisible}
        onCancel={() => setMeituanMerchantModifyVisible(false)}
        onOk={handleMerchantModifyMeituanOrder}
        okText="确认改约"
        cancelText="关闭"
        confirmLoading={meituanMerchantModifySubmitting}
        width={720}
        destroyOnHidden
      >
        <Space orientation="vertical" size={14} style={{ width: '100%' }}>
          <Descriptions column={2} size="small" bordered>
            <Descriptions.Item label="预约单号">{meituanQueryOrder?.externalOrderId || '-'}</Descriptions.Item>
            <Descriptions.Item label="美团订单号">{meituanQueryOrder?.externalReservationNo || '-'}</Descriptions.Item>
            <Descriptions.Item label="平台订单号">{meituanQueryOrder?.orderNo || '-'}</Descriptions.Item>
            <Descriptions.Item label="当前服务时段">{meituanQueryOrder?.serviceTimeDesc || '-'}</Descriptions.Item>
            <Descriptions.Item label="当前服务地址" span={2}>{meituanQueryOrder?.addressText || '-'}</Descriptions.Item>
          </Descriptions>
          <Form form={meituanMerchantModifyForm} layout="vertical">
            <Form.Item
              name="bookTimeRange"
              label="预约时间"
              rules={[{ required: true, message: '请选择预约时间' }]}
            >
              <RangePicker
                showTime
                format="YYYY-MM-DD HH:mm"
                style={{ width: '100%' }}
                placeholder={['预约开始时间', '预约结束时间']}
              />
            </Form.Item>
            <Space size={12} style={{ width: '100%' }} align="start">
              <Form.Item
                name="reserveUserName"
                label="预约人姓名"
                rules={[{ required: true, message: '请输入预约人姓名' }]}
                style={{ flex: 1 }}
              >
                <Input placeholder="请输入预约人姓名" />
              </Form.Item>
              <Form.Item
                name="reservePhone"
                label="预约人电话"
                rules={[{ required: true, message: '请输入预约人电话' }]}
                style={{ flex: 1 }}
              >
                <Input placeholder="请输入预约人电话" />
              </Form.Item>
            </Space>
            <Form.Item
              label="预约地址"
              required
            >
              <Space size={12} style={{ width: '100%' }} align="start">
                <Form.Item
                  name="provinceName"
                  rules={[{ required: true, message: '请输入省' }]}
                  style={{ flex: 1, marginBottom: 0 }}
                >
                  <Input placeholder="省" />
                </Form.Item>
                <Form.Item
                  name="cityName"
                  rules={[{ required: true, message: '请输入市' }]}
                  style={{ flex: 1, marginBottom: 0 }}
                >
                  <Input placeholder="市" />
                </Form.Item>
                <Form.Item
                  name="districtName"
                  rules={[{ required: true, message: '请输入区' }]}
                  style={{ flex: 1, marginBottom: 0 }}
                >
                  <Input placeholder="区" />
                </Form.Item>
              </Space>
            </Form.Item>
            <Form.Item
              name="reserveAddress"
              label="详细地址"
              rules={[{ required: true, message: '请输入详细地址' }]}
            >
              <Input.TextArea rows={2} placeholder="请输入详细地址，不要填写省市区" maxLength={300} showCount />
            </Form.Item>
            <Space size={12} style={{ width: '100%' }} align="start">
              <Form.Item name="deliveryUserName" label="送件姓名" style={{ flex: 1 }}>
                <Input placeholder="非必填" />
              </Form.Item>
              <Form.Item name="deliveryPhone" label="送件电话" style={{ flex: 1 }}>
                <Input placeholder="非必填" />
              </Form.Item>
            </Space>
            <Form.Item name="deliveryAddress" label="送件地址">
              <Input.TextArea rows={2} placeholder="非必填" maxLength={300} showCount />
            </Form.Item>
          </Form>
        </Space>
      </Modal>

      <Modal
        title="编辑结算流水"
        open={settlementEditVisible}
        onCancel={() => setSettlementEditVisible(false)}
        onOk={() => void handleSubmitSettlementEdit()}
        okText="保存"
        cancelText="取消"
        confirmLoading={settlementEditSubmitting}
        destroyOnHidden
      >
        <Form form={settlementEditForm} layout="vertical">
          <Form.Item
            name="settlementAmount"
            label="结算金额"
            rules={[{ required: true, message: '请输入结算金额' }]}
          >
            <InputNumber min={0} precision={2} style={{ width: '100%' }} placeholder="请输入结算金额" />
          </Form.Item>
          <Form.Item
            name="completedAt"
            label="完成时间"
            rules={[{ required: true, message: '请选择完成时间' }]}
          >
            <DatePicker showTime style={{ width: '100%' }} format="YYYY-MM-DD HH:mm:ss" />
          </Form.Item>
          <Form.Item
            name="eligibleSettleAt"
            label="可结算时间"
            rules={[{ required: true, message: '请选择可结算时间' }]}
          >
            <DatePicker showTime style={{ width: '100%' }} format="YYYY-MM-DD HH:mm:ss" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="售后扣费"
        open={afterSaleDeductionVisible}
        onCancel={() => setAfterSaleDeductionVisible(false)}
        onOk={() => void handleSubmitAfterSaleDeduction()}
        okText="生成扣费流水"
        cancelText="取消"
        confirmLoading={afterSaleDeductionSubmitting}
        destroyOnHidden
      >
        <Form form={afterSaleDeductionForm} layout="vertical">
          <Form.Item
            name="settlementAmount"
            label="扣费金额"
            rules={[
              { required: true, message: '请输入扣费金额' },
              {
                validator: async (_, value) => {
                  if (value === undefined || value === null || value === '') {
                    return;
                  }
                  if (Number(value) >= 0) {
                    throw new Error('扣费金额必须是负数');
                  }
                },
              },
            ]}
            extra="必须手动输入负数，例如 -20.00"
          >
            <InputNumber precision={2} style={{ width: '100%' }} placeholder="请输入负数金额，如 -20.00" />
          </Form.Item>
          <Form.Item name="remark" label="备注">
            <Input.TextArea rows={3} maxLength={200} placeholder="默认：售后扣费" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="订单加价"
        open={meituanAddPriceVisible}
        onCancel={() => setMeituanAddPriceVisible(false)}
        onOk={handleSubmitMeituanAddPrice}
        okText="发起补价"
        cancelText="关闭"
        confirmLoading={meituanAddPriceSubmitting}
        width={760}
        destroyOnHidden
      >
        <Space orientation="vertical" size={14} style={{ width: '100%' }}>
          <Descriptions column={2} size="small" bordered>
            <Descriptions.Item label="预约单号">{meituanQueryOrder?.externalOrderId || '-'}</Descriptions.Item>
            <Descriptions.Item label="美团订单号">{meituanQueryOrder?.externalReservationNo || '-'}</Descriptions.Item>
            <Descriptions.Item label="平台订单号">{meituanQueryOrder?.orderNo || '-'}</Descriptions.Item>
            <Descriptions.Item label="当前补价支付">{renderMeituanAddPricePaidTag(meituanQueryOrder?.meituanAddPricePaid)}</Descriptions.Item>
            <Descriptions.Item label="当前补价金额">
              {Number(meituanQueryOrder?.meituanAddPriceAmount || 0) > 0 ? `¥${Number(meituanQueryOrder?.meituanAddPriceAmount || 0).toFixed(2)}` : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="当前补价链接">
              {meituanQueryOrder?.meituanAddPriceUrl ? (
                <a href={meituanQueryOrder.meituanAddPriceUrl} target="_blank" rel="noreferrer">打开链接</a>
              ) : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="当前补价成功时间">
              {meituanQueryOrder?.meituanAddPriceSuccessTime || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="当前补价券码">
              {meituanQueryOrder?.meituanAddPriceSerialNumber || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="当前补价透传参数" span={2}>
              {meituanQueryOrder?.meituanAddPriceThirdPartyPaymentInfo || '-'}
            </Descriptions.Item>
          </Descriptions>
          <Form form={meituanAddPriceForm} layout="vertical">
            <Card size="small" title="加价明细">
              <Space size={12} style={{ width: '100%' }} align="start">
                <Form.Item name="addPriceItemId" label="三方加价明细ID" style={{ flex: 1 }}>
                  <Input placeholder="默认使用当前订单ID，可修改" />
                </Form.Item>
                <Form.Item
                  name="addPriceUnitPriceYuan"
                  label="加价金额(元)"
                  style={{ flex: 1 }}
                  rules={[{
                    validator: (_, value) => {
                      if (value === undefined || value === null || value === '') {
                        return Promise.resolve();
                      }
                      return Number(value) > 0 ? Promise.resolve() : Promise.reject(new Error('金额必须大于0'));
                    },
                  }]}
                >
                  <InputNumber min={0} precision={2} style={{ width: '100%' }} placeholder="如 10.00" />
                </Form.Item>
                <Form.Item name="addPriceSize" label="购买量" initialValue={1} style={{ width: 120 }}>
                  <InputNumber min={1} precision={0} style={{ width: '100%' }} />
                </Form.Item>
              </Space>
            </Card>
          </Form>
        </Space>
      </Modal>

      <Modal
        title="美团改约确认"
        open={meituanChangeVisible}
        onCancel={() => setMeituanChangeVisible(false)}
        onOk={handleConfirmMeituanChange}
        okText="同意改约"
        cancelText="取消"
        confirmLoading={meituanChangeSubmitting}
        okButtonProps={{ disabled: !meituanChangeRecord || meituanChangeRecord.processStatus === 1 }}
        footer={(_, { OkBtn, CancelBtn }) => (
          <Space>
            <Button
              danger
              disabled={!meituanChangeRecord || meituanChangeRecord.processStatus === 1}
              loading={meituanChangeSubmitting}
              onClick={handleRejectMeituanChange}
            >
              拒绝改约
            </Button>
            <CancelBtn />
            <OkBtn />
          </Space>
        )}
        width={760}
        destroyOnHidden
      >
        <Card loading={meituanChangeLoading} size="small">
          {meituanChangeRecord ? (
            <Descriptions column={2} size="small" bordered>
              <Descriptions.Item label="美团订单ID">{meituanChangeRecord.meituanOrderId || '-'}</Descriptions.Item>
              <Descriptions.Item label="处理状态">
                {meituanChangeRecord.processStatus === 1 ? '已处理' : meituanChangeRecord.processStatus === 2 ? '处理失败' : '待处理'}
              </Descriptions.Item>
              <Descriptions.Item label="原服务日期">
                {meituanQueryOrder?.serviceDateStart || '-'} 至 {meituanQueryOrder?.serviceDateEnd || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="原服务时段">{meituanQueryOrder?.serviceTimeDesc || '-'}</Descriptions.Item>
              <Descriptions.Item label="新开始时间">{meituanChangeRecord.reserveBeginTime || '-'}</Descriptions.Item>
              <Descriptions.Item label="新结束时间">{meituanChangeRecord.reserveEndTime || '-'}</Descriptions.Item>
              <Descriptions.Item label="联系人">{meituanChangeRecord.userName || meituanQueryOrder?.contactName || '-'}</Descriptions.Item>
              <Descriptions.Item label="联系电话">
                {meituanChangeRecord.reservePhone || meituanChangeRecord.reserveMobile || meituanQueryOrder?.contactPhone || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="产品名称">{meituanChangeRecord.productName || meituanQueryOrder?.productName || '-'}</Descriptions.Item>
              <Descriptions.Item label="备注">{meituanChangeRecord.remarks || '-'}</Descriptions.Item>
              <Descriptions.Item label="新服务地址" span={2}>
                {meituanChangeRecord.userAddress || meituanQueryOrder?.addressText || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="处理说明" span={2}>
                {meituanChangeRecord.processMessage || '-'}
              </Descriptions.Item>
            </Descriptions>
          ) : (
            <div>暂无美团改约记录</div>
          )}
        </Card>
      </Modal>

      <Modal
        title="分摊金额查询"
        open={meituanAmountQueryVisible}
        onCancel={() => setMeituanAmountQueryVisible(false)}
        footer={null}
        width={820}
        destroyOnHidden
      >
        <Space orientation="vertical" size={14} style={{ width: '100%' }}>
          <Descriptions column={2} size="small" bordered>
            <Descriptions.Item label="平台订单号">
              {meituanPaymentSharesResult?.serviceOrderNo || meituanQueryOrder?.orderNo || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="美团订单ID">
              {meituanPaymentSharesResult?.orderId || meituanQueryOrder?.externalOrderId || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="TraceID" span={2}>
              {meituanPaymentSharesResult?.traceId || '-'}
            </Descriptions.Item>
          </Descriptions>
          <Table
            rowKey={(record) => `${record.receiptCode || 'receipt'}-${record.amountType || 0}-${record.amount || 0}-${record.platformAmount || ''}-${record.merchantAmount || ''}`}
            size="small"
            loading={meituanPaymentSharesLoading}
            dataSource={meituanPaymentSharesResult?.result || []}
            pagination={false}
            locale={{ emptyText: meituanPaymentSharesLoading ? '查询中' : '暂无分摊金额数据' }}
            columns={[
              {
                title: '券码',
                dataIndex: 'receiptCode',
                width: 150,
                render: (value: string) => value || '-',
              },
              {
                title: '分摊金额',
                dataIndex: 'amount',
                width: 110,
                render: (value: number) => Number(value || 0).toFixed(2),
              },
              {
                title: '分摊类型',
                dataIndex: 'amountType',
                render: (value: number) => meituanPaymentShareTypeMap[Number(value)] || value || '-',
              },
              {
                title: '平台分摊',
                dataIndex: 'platformAmount',
                width: 110,
                render: (value: string) => value || '0',
              },
              {
                title: '商家分摊',
                dataIndex: 'merchantAmount',
                width: 110,
                render: (value: string) => value || '0',
              },
            ]}
          />
        </Space>
      </Modal>

      <Modal
        title="订单及券码状态查询"
        open={meituanCouponQueryVisible}
        onCancel={() => setMeituanCouponQueryVisible(false)}
        footer={null}
        width={1080}
        destroyOnHidden
      >
        <Space orientation="vertical" size={14} style={{ width: '100%' }}>
          <Card loading={meituanOrderInfoLoading} size="small">
            <Descriptions column={2} size="small" bordered>
              <Descriptions.Item label="平台订单号">
                {meituanOrderInfoResult?.serviceOrderNo || meituanQueryOrder?.orderNo || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="美团订单号">
                {meituanOrderInfoResult?.unifiedOrderId || meituanQueryOrder?.externalReservationNo || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="预约单号">
                {meituanQueryOrder?.externalOrderId || meituanOrderInfoResult?.orderId || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="订单状态">
                {(() => {
                  const status = Number(meituanOrderInfoResult?.order?.status || 0);
                  const config = meituanOrderInfoStatusMap[status] || { color: 'default', text: status ? String(status) : '-' };
                  return <Tag color={config.color}>{config.text}</Tag>;
                })()}
              </Descriptions.Item>
              <Descriptions.Item label="订单总金额">{getMeituanFieldText(meituanOrderInfoResult?.order, 'amount')}</Descriptions.Item>
              <Descriptions.Item label="订单实付金额">{getMeituanFieldText(meituanOrderInfoResult?.order, 'paidAmount')}</Descriptions.Item>
              <Descriptions.Item label="商家营销费">{getMeituanFieldText(meituanOrderInfoResult?.order, 'merchantAmount')}</Descriptions.Item>
              <Descriptions.Item label="下单门店ID">{getMeituanFieldText(meituanOrderInfoResult?.order, 'opPoiId')}</Descriptions.Item>
              <Descriptions.Item label="订单渠道">{getMeituanFieldText(meituanOrderInfoResult?.order, 'orderChannel')}</Descriptions.Item>
              <Descriptions.Item label="创建时间">{formatMeituanTimestamp(meituanOrderInfoResult?.order?.addTime)}</Descriptions.Item>
              <Descriptions.Item label="支付时间">{formatMeituanTimestamp(meituanOrderInfoResult?.order?.paySuccessTime)}</Descriptions.Item>
            </Descriptions>
          </Card>
          <Table<MeituanOrderQueryInfoReceipt>
            rowKey={(record) => `${record.receiptCode || 'receipt'}-${record.serialNumber || ''}-${record.receiptStatus || record.status || 0}-${record.receiptBeginTime || ''}`}
            size="small"
            loading={meituanOrderInfoLoading}
            dataSource={meituanOrderReceiptList}
            pagination={false}
            locale={{ emptyText: meituanOrderInfoLoading ? '查询中' : '暂无券码数据' }}
            columns={[
              {
                title: '券码号',
                dataIndex: 'receiptCode',
                width: 180,
                render: (value: string) => value || '-',
              },
              {
                title: '券码状态',
                dataIndex: 'status',
                width: 150,
                render: (value: number) => {
                  const config = meituanReceiptStatusMap[Number(value)] || { color: 'default', text: value ? String(value) : '-' };
                  return <Tag color={config.color}>{config.text}</Tag>;
                },
              },
              {
                title: '可用开始时间',
                dataIndex: 'receiptBeginTime',
                width: 170,
                render: formatMeituanTimestamp,
              },
              {
                title: '可用截止时间',
                dataIndex: 'receiptEndTime',
                width: 170,
                render: formatMeituanTimestamp,
              },
              {
                title: '是否关联预订',
                dataIndex: 'relatedBook',
                width: 120,
                render: (value: boolean) => value ? '是' : '否',
              },
              {
                title: '金额分摊',
                dataIndex: 'amountShareList',
                render: (_value: Array<Record<string, unknown>>, _record, index) => index === 0 ? renderMeituanAmountShareTags(meituanOrderAmountShareList) : '-',
              },
            ]}
          />
          <Table<Record<string, unknown>>
            rowKey={(record) => `${String(record.payBatchNo || 'amount')}-${String(record.amountType || 'type')}-${String(record.amount || '')}`}
            size="small"
            title={() => '订单下单金额明细'}
            loading={meituanOrderInfoLoading}
            dataSource={meituanOrderInfoResult?.orderPreAmountList || []}
            pagination={false}
            locale={{ emptyText: meituanOrderInfoLoading ? '查询中' : '暂无金额明细数据' }}
            columns={[
              { title: '支付批次', dataIndex: 'payBatchNo', width: 120, render: value => value || '-' },
              {
                title: '金额类型',
                dataIndex: 'amountType',
                width: 220,
                render: value => meituanPaymentShareTypeMap[Number(value || 0)] || value || '-',
              },
              {
                title: '金额',
                dataIndex: 'amount',
                width: 140,
                render: formatMeituanFenAmount,
              },
            ]}
          />
          <Table<Record<string, unknown>>
            rowKey={(record) => `${String(record.applyId || 'refund')}-${String(record.applyStatus || '')}-${String(record.refundSuccessTime || '')}-${String(record.refundAmount || '')}`}
            size="small"
            title={() => '退款申请单'}
            loading={meituanOrderInfoLoading}
            dataSource={meituanOrderInfoResult?.refundApplicationList || []}
            pagination={false}
            locale={{ emptyText: meituanOrderInfoLoading ? '查询中' : '暂无退款数据' }}
            columns={[
              { title: '退款单ID', dataIndex: 'applyId', width: 180, render: value => value || '-' },
              { title: '退款状态', dataIndex: 'applyStatus', width: 140, render: value => value || '-' },
              { title: '退款金额', dataIndex: 'refundAmount', width: 120, render: value => value || '-' },
              { title: '退款完成时间', dataIndex: 'refundSuccessTime', width: 180, render: formatMeituanTimestamp },
            ]}
          />
        </Space>
      </Modal>

      <Modal
        title="用户评论"
        open={meituanCommentVisible}
        onCancel={() => setMeituanCommentVisible(false)}
        footer={null}
        width={1280}
        destroyOnHidden
      >
        <Space orientation="vertical" size={14} style={{ width: '100%' }}>
          <Form form={meituanCommentForm} layout="inline">
            <Form.Item name="star" label="星级">
              <Select style={{ width: 120 }}>
                <Option value={1}>全部</Option>
                <Option value={2}>好评</Option>
                <Option value={3}>中差评</Option>
              </Select>
            </Form.Item>
            <Form.Item name="platform" label="平台">
              <Select style={{ width: 120 }}>
                <Option value={2}>美团</Option>
                <Option value={1}>大众点评</Option>
              </Select>
            </Form.Item>
            <Form.Item name="reviewTimeRange" label="评论时间" rules={[{ required: true, message: '请选择评论时间' }]}>
              <RangePicker showTime style={{ width: 360 }} />
            </Form.Item>
            <Form.Item>
              <Button type="primary" loading={meituanCommentLoading} onClick={() => loadMeituanShopReviews(1, meituanCommentPagination.pageSize)}>
                查询
              </Button>
            </Form.Item>
          </Form>
          <Descriptions column={2} size="small" bordered>
            <Descriptions.Item label="平台订单号">{meituanQueryOrder?.orderNo || '-'}</Descriptions.Item>
            <Descriptions.Item label="美团订单号">
              <Space>
                <span>{currentMeituanOrderNo || '-'}</span>
                <Button
                  size="small"
                  type={meituanCommentLocalFilterEnabled ? 'primary' : 'default'}
                  disabled={!currentMeituanOrderNo || !meituanCommentResult}
                  onClick={() => setMeituanCommentLocalFilterEnabled(true)}
                  style={{
                    borderRadius: 999,
                    paddingInline: 12,
                    background: meituanCommentLocalFilterEnabled ? '#1677ff' : '#eef6ff',
                    borderColor: meituanCommentLocalFilterEnabled ? '#1677ff' : '#91caff',
                    color: meituanCommentLocalFilterEnabled ? '#fff' : '#0958d9',
                    boxShadow: meituanCommentLocalFilterEnabled ? '0 4px 12px rgba(22, 119, 255, 0.22)' : 'none',
                  }}
                >
                  当前美团订单查询
                </Button>
                {meituanCommentLocalFilterEnabled && (
                  <Button size="small" type="link" onClick={() => setMeituanCommentLocalFilterEnabled(false)}>
                    查看全部
                  </Button>
                )}
              </Space>
            </Descriptions.Item>
          </Descriptions>
          <Table<MeituanShopReviewItem>
            rowKey="reviewId"
            size="small"
            loading={meituanCommentLoading}
            dataSource={filteredMeituanCommentList}
            scroll={{ x: 1320, y: 560 }}
            pagination={{
              current: meituanCommentPagination.page,
              pageSize: meituanCommentPagination.pageSize,
              total: filteredMeituanCommentList.length === meituanCommentPagination.pageSize && !meituanCommentLocalFilterEnabled
                ? meituanCommentPagination.page * meituanCommentPagination.pageSize + 1
                : (meituanCommentPagination.page - 1) * meituanCommentPagination.pageSize + filteredMeituanCommentList.length,
              showSizeChanger: !meituanCommentLocalFilterEnabled,
              onChange: (page, pageSize) => {
                if (!meituanCommentLocalFilterEnabled) {
                  loadMeituanShopReviews(page, pageSize);
                }
              },
            }}
            columns={[
              {
                title: '评价ID',
                dataIndex: 'reviewId',
                width: 130,
                render: (value: string) => value || '-',
              },
              {
                title: '星级',
                dataIndex: 'accurateStar',
                width: 80,
                render: (value: number, record: MeituanShopReviewItem) => (
                  <Tag color="gold">{meituanReviewStarMap[value] || meituanReviewStarMap[record.star] || value || '-'}</Tag>
                ),
              },
              {
                title: '评价时间',
                dataIndex: 'reviewTime',
                width: 160,
                render: (value: string) => value || '-',
              },
              {
                title: '美团订单号',
                dataIndex: 'consumeOrderId',
                width: 190,
                render: (value: string) => value || '-',
              },
              {
                title: '消费金额',
                dataIndex: 'consumeAmount',
                width: 90,
                render: (value: string) => value || '-',
              },
              {
                title: '券号',
                dataIndex: 'serialNumbers',
                width: 150,
                render: (value: string) => value || '-',
              },
              {
                title: '评分详情',
                dataIndex: 'scoreDetails',
                width: 240,
                render: (value: MeituanShopReviewItem['scoreDetails']) => renderMeituanReviewPairs(value || []),
              },
              {
                title: '交易信息',
                dataIndex: 'tradeDetails',
                width: 280,
                render: (value: MeituanShopReviewItem['tradeDetails']) => renderMeituanReviewPairs(value || []),
              },
            ]}
          />
        </Space>
      </Modal>

      <Modal
        title="美团预约失败恢复"
        open={meituanRecoverVisible}
        onCancel={() => {
          if (!meituanRecoverSubmitting) {
            setMeituanRecoverVisible(false);
          }
        }}
        onOk={() => void handleRecoverMeituanReservationOrder()}
        confirmLoading={meituanRecoverSubmitting}
        okText="恢复"
        destroyOnHidden
      >
        <Form form={meituanRecoverForm} layout="vertical">
          <Form.Item
            label="美团预约单号"
            name="meituanOrderId"
            rules={[{ required: true, message: '请输入美团预约单号' }]}
          >
            <Input placeholder="请输入 meituan/reservation-order-logs 中失败的美团预约单号" allowClear />
          </Form.Item>
        </Form>
      </Modal>

    </Card>
  );
};

export default PaymentOrderPage;





